// src/components/VisionChatInterface.jsx
// Vision Assist polish: AskLawly dashboard UI, anchored response viewport, 40-message history pagination with Chat-matched arrows, compact multi-screen strips, transcript-only plus menu, manual attachments disabled, ChatInput-matched STT/send controls, streaming /speak TTS — 2026.09.13-v4
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  memo,
  useCallback,
} from "react";
import { FaVolumeUp, FaCopy, FaEdit, FaPlus, FaTimes, FaCheck } from "react-icons/fa";
import { createPortal } from "react-dom";
import { FiMic, FiArrowUp, FiChevronUp, FiChevronDown } from "react-icons/fi";

import AttachmentMenu from "./AttachmentMenu";
import AttachmentController from "./AttachmentController";
import FileIcon from "../components/FileIcon";
import FileViewerModal from "./FileViewerModal";
import AnswerRenderer from "./answer/AnswerRenderer";

import { getAuth, onAuthStateChanged } from "firebase/auth";

import {
  sendVisionChatMessage,
  sendVisionClientEvent,
  getVisionMessages,
  getVisionSession,
  getVisionFrameUrl,
  exportVisionTranscriptPdf,
} from "../utils/visionAssistApi";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Vision Assist is intentionally screen-centric. Manual file/photo/camera
// attachments remain preserved in shared code for future product changes but
// are disabled at this interface boundary. Screen-share frames use their own
// dedicated Vision pipeline and are unaffected.
const VISION_MANUAL_ATTACHMENTS_ENABLED = false;

// Match ChatInput's compact composer/send-control contract.
const TEXTAREA_DEFAULT_HEIGHT = 48;
const TEXTAREA_MAX_HEIGHT = 120;
const TEXTAREA_GROW_TOLERANCE_PX = 2;

const VISION_HISTORY_PAGE_SIZE = 40;
const VISION_JUMP_TO_LATEST_DISTANCE_PX = 240;

// -----------------------------------------------------------------------------
// LEXA READ-ALOUD — STREAMING PCM
// -----------------------------------------------------------------------------
// Primary:
//   authenticated POST /speak
//   -> backend dominant-language/code-switch guard
//   -> Google Chirp 3 HD / Erinome
//   -> streamed raw PCM
//   -> Web Audio playback as bytes arrive
//
// Fallback:
//   browser speechSynthesis only when backend/provider/stream/playback fails.
//
// Frontend responsibility:
// - Determine only the DOMINANT language hint for the current Lexa answer.
// - Do not attempt final mixed-language segmentation here.
// - Browser fallback receives aggressively cleaned text so Markdown/punctuation
//   such as ###, **, bullets, raw URLs, "comma", "stop", etc. are not spoken.

const TTS_ROUTE = "/speak";
const TTS_DEFAULT_LANGUAGE = "en-US";

// Must match tts_handler.py.
const TTS_PCM_SAMPLE_RATE_HZ = 24000;
const TTS_PCM_CHANNELS = 1;
const TTS_PCM_SAMPLE_WIDTH_BYTES = 2;

// Backend prefetches the first Google audio chunk before HTTP 200.
// Immediate HTTP/network failures fall back immediately. An unresolved/cold
// request gets enough room to start without making the user wait indefinitely.
const TTS_FIRST_AUDIO_TIMEOUT_MS = 18000;

// Once streaming has begun, abort only if the byte stream itself stops making
// progress for an unreasonable period.
const TTS_STREAM_STALL_TIMEOUT_MS = 20000;

// Streaming playback smoothing.
//
// We deliberately keep only a small rolling reserve. This remains dramatically
// faster than waiting for a complete MP3, while reducing underruns/clicks caused
// by arbitrary HTTP chunk timing.
//
// Startup reserve:
//   220 ms before first Web Audio source is scheduled.
//
// Steady block size:
//   120 ms chunks. Network reads are arbitrary; Web Audio should receive stable
//   PCM-sized blocks instead of one AudioBufferSource per fetch chunk.
//
// Scheduling lead:
//   keep at least ~80 ms queued ahead of context.currentTime.
const TTS_PCM_START_BUFFER_MS = 220;
const TTS_PCM_PLAYBACK_BLOCK_MS = 120;
const TTS_PCM_MIN_SCHEDULE_LEAD_MS = 80;

const TTS_PCM_BYTES_PER_MS =
  (TTS_PCM_SAMPLE_RATE_HZ *
    TTS_PCM_CHANNELS *
    TTS_PCM_SAMPLE_WIDTH_BYTES) /
  1000;

const TTS_PCM_START_BUFFER_BYTES = Math.max(
  TTS_PCM_SAMPLE_WIDTH_BYTES,
  Math.floor(
    TTS_PCM_BYTES_PER_MS *
      TTS_PCM_START_BUFFER_MS
  )
);

const TTS_PCM_PLAYBACK_BLOCK_BYTES = Math.max(
  TTS_PCM_SAMPLE_WIDTH_BYTES,
  Math.floor(
    TTS_PCM_BYTES_PER_MS *
      TTS_PCM_PLAYBACK_BLOCK_MS
  )
);

const TTS_LANGUAGE_DEFAULTS = {
  af: "af-ZA",
  ar: "ar-XA",
  bg: "bg-BG",
  bn: "bn-IN",
  ca: "ca-ES",
  cs: "cs-CZ",
  da: "da-DK",
  de: "de-DE",
  el: "el-GR",
  en: "en-US",
  es: "es-US",
  et: "et-EE",
  fi: "fi-FI",
  fr: "fr-FR",
  gu: "gu-IN",
  he: "he-IL",
  hi: "hi-IN",
  hr: "hr-HR",
  hu: "hu-HU",
  id: "id-ID",
  it: "it-IT",
  ja: "ja-JP",
  kn: "kn-IN",
  ko: "ko-KR",
  lt: "lt-LT",
  lv: "lv-LV",
  ml: "ml-IN",
  mr: "mr-IN",
  ms: "ms-MY",
  nb: "nb-NO",
  nl: "nl-NL",
  no: "nb-NO",
  pa: "pa-IN",
  pl: "pl-PL",
  pt: "pt-BR",
  ro: "ro-RO",
  ru: "ru-RU",
  sk: "sk-SK",
  sl: "sl-SI",
  sr: "sr-RS",
  sv: "sv-SE",
  sw: "sw-KE",
  ta: "ta-IN",
  te: "te-IN",
  th: "th-TH",
  tr: "tr-TR",
  uk: "uk-UA",
  ur: "ur-IN",
  vi: "vi-VN",
  yue: "yue-HK",
  zh: "cmn-CN",
  cmn: "cmn-CN",
};

const TTS_LOCALE_ALIASES = {
  "bn-BD": "bn-IN",
  "ar-001": "ar-XA",
  "zh-CN": "cmn-CN",
  "zh-SG": "cmn-CN",
  "zh-Hans": "cmn-CN",
  "no-NO": "nb-NO",
};

// Browser SpeechSynthesisVoice has no standardized gender property. These names
// are only a best-effort emergency preference. Production Google TTS remains
// strictly Erinome/female.
const FEMALE_BROWSER_VOICE_RE =
  /\b(?:female|samantha|ava|zoe|allison|victoria|karen|moira|tessa|veena|fiona|zira|susan|hazel|helena|aria|jenny|sonia|natasha|monica|paulina|amelie|audrey)\b/i;

function normalizeTtsLocale(value) {
  const raw = String(value || "").trim().replace(/_/g, "-");
  if (!raw) return "";

  const parts = raw.split("-").filter(Boolean);
  if (!parts.length) return "";

  const base = parts[0].toLowerCase();

  if (parts.length === 1) {
    return TTS_LANGUAGE_DEFAULTS[base] || "";
  }

  const normalized = [base];

  for (const part of parts.slice(1)) {
    if (/^[A-Za-z]{4}$/.test(part)) {
      normalized.push(
        part[0].toUpperCase() +
          part.slice(1).toLowerCase()
      );
    } else if (
      /^[A-Za-z]{2,3}$/.test(part) ||
      /^\d{3}$/.test(part)
    ) {
      normalized.push(part.toUpperCase());
    } else {
      normalized.push(part);
    }
  }

  const locale = normalized.join("-");
  return TTS_LOCALE_ALIASES[locale] || locale;
}

function countTtsMatches(value, pattern) {
  try {
    return (String(value || "").match(pattern) || []).length;
  } catch {
    return 0;
  }
}

function scoreLatinSpeechLanguage(
  text,
  words,
  accentPattern = null
) {
  const source = ` ${String(text || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")} `;

  let score = 0;

  for (const word of words || []) {
    const escaped = String(word).replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );

    if (
      new RegExp(
        `(?:^|\\s)${escaped}(?:\\s|$)`,
        "i"
      ).test(source)
    ) {
      score += 1;
    }
  }

  if (
    accentPattern &&
    accentPattern.test(String(text || ""))
  ) {
    score += 2;
  }

  return score;
}

function inferTtsDominantLocale(
  text,
  explicitLanguage = "",
  uiLanguage = ""
) {
  const explicit =
    normalizeTtsLocale(explicitLanguage);

  // Message metadata, when present, is the strongest frontend hint. The backend
  // still performs the final code-switch guard and can correct mixed segments.
  if (explicit) return explicit;

  const source = String(text || "");

  const uiLocale =
    normalizeTtsLocale(uiLanguage);
  const uiBase =
    String(uiLocale || "")
      .split("-", 1)[0]
      .toLowerCase();

  const latinCount = countTtsMatches(
    source,
    /[A-Za-zÀ-ÖØ-öø-ÿŒœÆæ]/g
  );

  const hanCount = countTtsMatches(
    source,
    /[\u3400-\u4DBF\u4E00-\u9FFF]/g
  );
  const kanaCount = countTtsMatches(
    source,
    /[\u3040-\u30FF]/g
  );

  const scriptCandidates = [
    {
      locale: "bn-IN",
      count: countTtsMatches(
        source,
        /[\u0980-\u09FF]/g
      ),
    },
    {
      locale: "gu-IN",
      count: countTtsMatches(
        source,
        /[\u0A80-\u0AFF]/g
      ),
    },
    {
      locale: "pa-IN",
      count: countTtsMatches(
        source,
        /[\u0A00-\u0A7F]/g
      ),
    },
    {
      locale: "ta-IN",
      count: countTtsMatches(
        source,
        /[\u0B80-\u0BFF]/g
      ),
    },
    {
      locale: "te-IN",
      count: countTtsMatches(
        source,
        /[\u0C00-\u0C7F]/g
      ),
    },
    {
      locale: "kn-IN",
      count: countTtsMatches(
        source,
        /[\u0C80-\u0CFF]/g
      ),
    },
    {
      locale: "ml-IN",
      count: countTtsMatches(
        source,
        /[\u0D00-\u0D7F]/g
      ),
    },
    {
      locale: "th-TH",
      count: countTtsMatches(
        source,
        /[\u0E00-\u0E7F]/g
      ),
    },
    {
      locale: "he-IL",
      count: countTtsMatches(
        source,
        /[\u0590-\u05FF]/g
      ),
    },
    {
      locale: "el-GR",
      count: countTtsMatches(
        source,
        /[\u0370-\u03FF]/g
      ),
    },
    {
      locale: "ko-KR",
      count: countTtsMatches(
        source,
        /[\uAC00-\uD7AF]/g
      ),
    },
    {
      locale:
        uiBase === "ur" ||
        /[ٹڈڑںھہےۓ]/u.test(source)
          ? "ur-IN"
          : "ar-XA",
      count: countTtsMatches(
        source,
        /[\u0600-\u06FF\u0750-\u077F]/g
      ),
    },
    {
      locale:
        uiBase === "mr" ||
        /[ळऴ]/u.test(source)
          ? "mr-IN"
          : "hi-IN",
      count: countTtsMatches(
        source,
        /[\u0900-\u097F]/g
      ),
    },
    {
      locale:
        /[іїєґ]/iu.test(source)
          ? "uk-UA"
          : /[љњђћџ]/iu.test(source)
          ? "sr-RS"
          : "ru-RU",
      count: countTtsMatches(
        source,
        /[\u0400-\u04FF]/g
      ),
    },
    {
      locale: "ja-JP",
      count:
        kanaCount > 0
          ? kanaCount + hanCount
          : 0,
    },
    {
      locale:
        uiBase === "yue"
          ? "yue-HK"
          : "cmn-CN",
      count:
        kanaCount === 0
          ? hanCount
          : 0,
    },
  ].sort((a, b) => b.count - a.count);

  const strongestNonLatin =
    scriptCandidates[0];

  // This is a dominant-language hint, not "any matching character wins".
  // A mostly-English answer containing one Bengali word therefore remains an
  // English/Latin hint; a mostly-Bengali answer with a few English terms is bn-IN.
  if (
    strongestNonLatin?.count > 0 &&
    strongestNonLatin.count > latinCount
  ) {
    return strongestNonLatin.locale;
  }

  // Latin-language dominant inference for common AskLawly languages.
  const latinCandidates = [
    {
      locale: "es-US",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "que",
          "para",
          "con",
          "una",
          "como",
          "pero",
          "porque",
          "también",
          "usted",
          "esto",
          "esta",
        ],
        /[¿¡ñ]/i
      ),
    },
    {
      locale: "fr-FR",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "avec",
          "pour",
          "dans",
          "mais",
          "vous",
          "nous",
          "cette",
          "être",
          "peut",
          "également",
        ],
        /[àâçéèêëîïôûùüÿœ]/i
      ),
    },
    {
      locale: "de-DE",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "und",
          "der",
          "die",
          "das",
          "mit",
          "für",
          "nicht",
          "auch",
          "eine",
          "werden",
        ],
        /[äöüß]/i
      ),
    },
    {
      locale: "pt-BR",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "que",
          "para",
          "com",
          "uma",
          "não",
          "também",
          "você",
          "isso",
          "esta",
          "como",
        ],
        /[ãõçáéíóúâêô]/i
      ),
    },
    {
      locale: "it-IT",
      score: scoreLatinSpeechLanguage(
        source,
        [
          "che",
          "per",
          "con",
          "una",
          "non",
          "anche",
          "questo",
          "questa",
          "come",
          "sono",
        ],
        /[àèéìòù]/i
      ),
    },
  ].sort((a, b) => b.score - a.score);

  if (latinCandidates[0]?.score >= 3) {
    return latinCandidates[0].locale;
  }

  // Latin is dominant but no stronger alternate signal was found.
  if (latinCount > 0) {
    if (
      uiLocale &&
      ["en", "es", "fr", "de", "pt", "it"].includes(
        uiBase
      )
    ) {
      return uiLocale;
    }

    return "en-US";
  }

  if (strongestNonLatin?.count > 0) {
    return strongestNonLatin.locale;
  }

  return uiLocale || TTS_DEFAULT_LANGUAGE;
}

function browserQuoteMarkers(languageCode) {
  const base = String(languageCode || "")
    .split("-", 1)[0]
    .toLowerCase();

  const markers = {
    en: ["Quote", "End quote"],
    es: ["Cita", "Fin de la cita"],
    fr: ["Citation", "Fin de la citation"],
    bn: ["উদ্ধৃতি", "উদ্ধৃতি শেষ"],
    hi: ["उद्धरण", "उद्धरण समाप्त"],
    ar: ["اقتباس", "نهاية الاقتباس"],
    ur: ["اقتباس", "اقتباس ختم"],
    de: ["Zitat", "Ende des Zitats"],
    pt: ["Citação", "Fim da citação"],
    it: ["Citazione", "Fine della citazione"],
    ja: ["引用", "引用終わり"],
    ko: ["인용", "인용 끝"],
    ru: ["Цитата", "Конец цитаты"],
  };

  return markers[base] || ['"', '"'];
}

function stripBrowserLiteralPunctuation(value) {
  let out = String(value || "");

  // Keep decimal points such as 12.5 while removing punctuation that some
  // browser voices literally announce as "period", "stop", "comma", etc.
  const decimalToken =
    "__ASKLAWLY_DECIMAL_POINT__";

  out = out.replace(
    /(\d)\.(\d)/g,
    `$1${decimalToken}$2`
  );

  out = out
    .replace(/[!?;:]+/g, "\n")
    .replace(/,+/g, " ")
    .replace(/\.+/g, "\n");

  out = out.replace(
    new RegExp(decimalToken, "g"),
    "."
  );

  out = out
    .replace(
      /[#*_`~|\\/<>{}\[\]()]+/g,
      " "
    )
    .replace(/[–—]+/g, " ")
    .replace(/\s+-\s+/g, " ")
    .replace(/(^|\s)-(?=\s|$)/g, " ")
    .replace(/&/g, " ");

  // Ordinary quotation marks remain because quotation can carry meaning.
  return out;
}

function prepareBrowserSpeechText(
  text,
  languageCode = TTS_DEFAULT_LANGUAGE
) {
  let value = String(text || "")
    .replace(/\r\n?/g, "\n")
    .trim();

  if (!value) return "";

  value = value.replace(
    /<!--[\s\S]*?-->/g,
    ""
  );

  value = value.replace(
    /(?:cite|filecite|url|entity|product|products|navlist|image_group|video|genui).*?/g,
    ""
  );

  // Fenced code is visual content, not read-aloud prose.
  value = value.replace(
    /```[\s\S]*?```|~~~[\s\S]*?~~~/g,
    "\n"
  );

  // Final Sources/References/Citations/Follow-up sections are not spoken.
  const sectionLines = value.split("\n");
  let cutIndex = -1;

  for (
    let index = sectionLines.length - 1;
    index >= 0;
    index -= 1
  ) {
    if (
      /^\s{0,3}(?:#{1,6}\s*)?(?:sources?|references?|citations?|follow[- ]?up(?:\s+questions?)?)\s*:?\s*$/i.test(
        sectionLines[index] || ""
      )
    ) {
      cutIndex = index;
      break;
    }
  }

  if (cutIndex >= 0) {
    value = sectionLines
      .slice(0, cutIndex)
      .join("\n");
  }

  // Keep link/image labels, never raw URLs.
  value = value.replace(
    /!\[([^\]]*)\]\((?:[^()]|\([^)]*\))*\)/g,
    "$1"
  );
  value = value.replace(
    /\[([^\]]+)\]\((?:[^()]|\([^)]*\))*\)/g,
    "$1"
  );
  value = value.replace(
    /https?:\/\/[^\s<>()]+/gi,
    ""
  );
  value = value.replace(
    /\[\^[^\]]+\]/g,
    ""
  );
  value = value.replace(
    /\[(?:\d+(?:\s*[-,]\s*\d+)*)\]/g,
    ""
  );

  const [quoteStart, quoteEnd] =
    browserQuoteMarkers(languageCode);

  const output = [];
  let inBlockQuote = false;

  const closeBlockQuote = () => {
    if (inBlockQuote) {
      output.push(quoteEnd);
      inBlockQuote = false;
    }
  };

  for (const rawLine of value.split("\n")) {
    const line = String(rawLine || "")
      .trimEnd();

    if (
      /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(
        line
      )
    ) {
      continue;
    }

    if (
      /^\s*(?:[-*_]\s*){3,}$/.test(line)
    ) {
      closeBlockQuote();
      continue;
    }

    const quoteMatch = line.match(
      /^\s{0,3}>\s?(.*)$/
    );

    if (quoteMatch) {
      if (!inBlockQuote) {
        output.push(quoteStart);
        inBlockQuote = true;
      }

      const quoteText = String(
        quoteMatch[1] || ""
      ).trim();

      if (quoteText) {
        output.push(quoteText);
      }
      continue;
    }

    closeBlockQuote();

    const heading = line.match(
      /^\s{0,3}#{1,6}\s+(.+?)\s*$/
    );

    if (heading) {
      output.push(heading[1].trim());
      output.push("");
      continue;
    }

    const unordered = line.match(
      /^\s*[-+*•]\s+(.*)$/
    );

    if (unordered) {
      const item = String(
        unordered[1] || ""
      )
        .replace(
          /^\[(?: |x|X)\]\s*/,
          ""
        )
        .trim();

      if (item) output.push(item);
      continue;
    }

    const ordered = line.match(
      /^\s*(\d+)[.)]\s+(.*)$/
    );

    if (ordered) {
      output.push(
        `${ordered[1]} ${ordered[2].trim()}`
      );
      continue;
    }

    if (line.includes("|")) {
      const cells = line
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim())
        .filter(Boolean);

      if (cells.length >= 2) {
        output.push(cells.join(" "));
        continue;
      }
    }

    output.push(line.trim());
  }

  closeBlockQuote();
  value = output.join("\n");

  value = value.replace(
    /`([^`\n]+)`/g,
    "$1"
  );
  value = value.replace(
    /\*\*(.+?)\*\*/g,
    "$1"
  );
  value = value.replace(
    /__(.+?)__/g,
    "$1"
  );
  value = value.replace(
    /~~(.+?)~~/g,
    "$1"
  );
  value = value.replace(
    /(?<!\*)\*([^*\n]+)\*(?!\*)/g,
    "$1"
  );
  value = value.replace(
    /(?<!_)_([^_\n]+)_(?!_)/g,
    "$1"
  );
  value = value.replace(
    /<[^>]+>/g,
    " "
  );

  // Never let the browser say pound/hash/asterisk presentation syntax.
  value = value.replace(
    /#\s*(\d+)/g,
    "$1"
  );
  value = value.replace(
    /#+/g,
    " "
  );

  value =
    stripBrowserLiteralPunctuation(value);

  value = value
    .replace(/[ \t]+/g, " ")
    .replace(
      /[ \t]*\n[ \t]*/g,
      "\n"
    )
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return value;
}

function getTtsBackendUrl() {
  const base = String(
    process.env.REACT_APP_BACKEND_URL || ""
  )
    .trim()
    .replace(/\/+$/, "");

  return base
    ? `${base}${TTS_ROUTE}`
    : "";
}

function concatUint8Arrays(left, right) {
  const a =
    left instanceof Uint8Array
      ? left
      : new Uint8Array(0);

  const b =
    right instanceof Uint8Array
      ? right
      : new Uint8Array(0);

  if (!a.length) return b.slice();
  if (!b.length) return a.slice();

  const joined = new Uint8Array(
    a.length + b.length
  );

  joined.set(a, 0);
  joined.set(b, a.length);

  return joined;
}

function pcm16LeBytesToFloat32(bytes) {
  const source =
    bytes instanceof Uint8Array
      ? bytes
      : new Uint8Array(0);

  const evenLength =
    source.length -
    (source.length %
      TTS_PCM_SAMPLE_WIDTH_BYTES);

  if (evenLength <= 0) {
    return new Float32Array(0);
  }

  const view = new DataView(
    source.buffer,
    source.byteOffset,
    evenLength
  );

  const samples = new Float32Array(
    evenLength /
      TTS_PCM_SAMPLE_WIDTH_BYTES
  );

  for (
    let offset = 0, sampleIndex = 0;
    offset < evenLength;
    offset += 2, sampleIndex += 1
  ) {
    const sample = view.getInt16(
      offset,
      true
    );

    samples[sampleIndex] =
      sample < 0
        ? sample / 32768
        : sample / 32767;
  }

  return samples;
}


/**
 * ✅ SOURCE OF TRUTH:
 * - Reads messages ONLY from backend: GET /vision/session/<id>/messages
 * - Sends messages ONLY through backend: POST /vision/session/<id>/chat
 * - Analyze also ONLY through backend helper: POST /vision/session/<id>/analyze
 * - NO Firestore listeners. NO Firestore reads/writes here.
 *
 * ✅ IMPORTANT FIX:
 * - DO NOT use client_created_at for createdMs (sorting/baseline).
 * - createdMs = server timeline only
 * - clientCreatedAt = stored separately for matching optimistic messages
 *
 * ✅ ORDERING FIX (THIS FILE):
 * - NEVER use Date.now() as a fallback for server timestamp ordering.
 * - If a backend message is missing created_at_ms / created_at, we preserve backend order
 *   using a synthetic monotonic sortMs (anchored by any real server timestamps).
 * - UI sorts by sortMs (NOT createdMs), preventing assistant bubbles appearing above user bubbles.
 *
 * ✅ RESPONSE DISPLAY FIX (THIS FILE):
 * - When multi-image analyze returns a JSON payload (e.g. {"assistant_reply": "...", "frame_summary": "..."}),
 *   DO NOT render the whole JSON "lump".
 * - Always render only the actual assistant text:
 *     reply_markdown || assistant_reply || text
 *   including if the backend stored that JSON as a STRING in text/message.
 *
 * ✅ DUPLICATE USER MESSAGE FIX (THIS FILE):
 * - We do optimistic local echo for the user's message.
 * - If backend response arrives late (e.g., analyze), time-based matching can fail and the local echo
 *   remains, causing the user's message to appear twice.
 * - We now maintain a pending local-send registry and remove the local echo as soon as the backend
 *   "user" message is observed (matched by clientCreatedAt if present, else by signature after baseline).
 *
 * ✅ BUSY / THINKING / ANALYZING RULE (IMPORTANT):
 * - This component MUST NOT decide "busy" on its own.
 * - It MUST strictly follow the parent's props:
 *     isBusy + busyMode
 * - While isBusy=true:
 *     - Send key MUST remain disabled (Enter + send button).
 *     - Lexa shows persistent "thinking…" or "analyzing…" based on busyMode.
 *
 * ✅ NEW UX RULES (THIS FILE):
 * 1) Busy bubble delay:
 *    - After a NEW assistant message arrives, suppress busy bubble for N ms.
 *    - After N ms, if parent still busy, show it. If not, never show (prevents flash/glitch).
 *
 * 2) Voice accept (IMPORTANT):
 *    - Clicking the tick should ACCEPT the voice transcript into the input box (no auto-send).
 *    - After acceptance, the up-arrow send becomes enabled and the user manually sends.
 *
 * ✅ HISTORY NAVIGATION (THIS FILE):
 * - Initial history is the newest 40 stored messages.
 * - Floating ↑ matches ChatInterface and loads the previous 40 using next_cursor.
 * - Older pages prepend without moving the user's reading position.
 * - Floating ↓ matches ChatInterface and is frontend-only; it jumps to latest.
 * - Polling the newest page never discards older pages already loaded.
 *
 * ✅ SESSION END / TIMEOUT UX (THIS FILE):
 * - Manual attachments are disabled for the entire Vision Assist lifecycle.
 * - The PLUS remains a utility menu and exposes Chat transcript only.
 * - When session is ended/expired (manual end OR 00:00 OR browser chose disable), disable chat input/send.
 *
 * ✅ MANUAL ATTACHMENT POLICY:
 * - The old Vision attachment implementation remains dormant for future reuse.
 * - AttachmentController is not mounted while VISION_MANUAL_ATTACHMENTS_ENABLED=false.
 * - Screen-share frame upload/analyze is a separate pipeline and is not changed here.
 */

// -------------------------------------
// Busy bubble delay after assistant message
// -------------------------------------
const BUSY_AFTER_ASSISTANT_DELAY_MS = 3000;

// -------------------------------------
// Debug log (counts for attachments)
// -------------------------------------
const VISION_CHAT_DEBUG = (() => {
  try {
    if (process.env.REACT_APP_VISION_CHAT_DEBUG === "1") return true;
    if (typeof window !== "undefined" && window?.localStorage) {
      return window.localStorage.getItem("VISION_CHAT_DEBUG") === "1";
    }
    return false;
  } catch {
    return process.env.REACT_APP_VISION_CHAT_DEBUG === "1";
  }
})();

function _chatDbg(...args) {
  if (!VISION_CHAT_DEBUG) return;
  try {
    // eslint-disable-next-line no-console
    console.log(...args);
  } catch {}
}

function _trimStr(v) {
  return typeof v === "string" ? v.trim() : "";
}

function _uniqCleanStrArray(arr) {
  try {
    if (!Array.isArray(arr)) return [];
    const cleaned = arr.map((x) => String(x || "").trim()).filter(Boolean);
    return Array.from(new Set(cleaned));
  } catch {
    return [];
  }
}

function _frameIdFromAny(value) {
  try {
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return "";
      if (/^(?:https?:|data:|gs:)/i.test(s)) return "";
      return s;
    }

    if (!value || typeof value !== "object") return "";

    return String(
      value.frame_id ||
        value.frameId ||
        value.id ||
        value.frame ||
        ""
    ).trim();
  } catch {
    return "";
  }
}

function _extractFrameIdsFromMessage(raw) {
  try {
    const r = raw && typeof raw === "object" ? raw : {};
    const attachment = r.attachment && typeof r.attachment === "object" ? r.attachment : {};
    const meta = r.meta && typeof r.meta === "object" ? r.meta : {};
    const metadata = r.metadata && typeof r.metadata === "object" ? r.metadata : {};

    const out = [];
    const pushOne = (value) => {
      const fid = _frameIdFromAny(value);
      if (fid) out.push(fid);
    };

    [
      r.frame_id,
      r.frameId,
      attachment.frame_id,
      attachment.frameId,
      meta.frame_id,
      meta.frameId,
      metadata.frame_id,
      metadata.frameId,
    ].forEach(pushOne);

    [
      r.frame_ids,
      r.frameIds,
      r.frames,
      attachment.frame_ids,
      attachment.frameIds,
      attachment.frames,
      meta.frame_ids,
      meta.frameIds,
      meta.frames,
      metadata.frame_ids,
      metadata.frameIds,
      metadata.frames,
    ].forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach(pushOne);
    });

    return Array.from(new Set(out.filter(Boolean)));
  } catch {
    return [];
  }
}

function _messageRepresentsMultiFrameAnalysis(message) {
  try {
    const m = message && typeof message === "object" ? message : {};
    const raw = m.raw && typeof m.raw === "object" ? m.raw : m;
    const meta =
      (raw.meta && typeof raw.meta === "object" && raw.meta) ||
      (raw.metadata && typeof raw.metadata === "object" && raw.metadata) ||
      (m.meta && typeof m.meta === "object" && m.meta) ||
      {};

    const frameIds = _uniqCleanStrArray([
      String(m.frame_id || raw.frame_id || raw.frameId || "").trim(),
      ...(Array.isArray(m.frame_ids) ? m.frame_ids : []),
      ..._extractFrameIdsFromMessage(raw),
    ]);

    if (frameIds.length > 1) return true;

    const frameCount = Number(
      meta.frame_count ??
        meta.frameCount ??
        raw.frame_count ??
        raw.frameCount ??
        0
    );
    if (Number.isFinite(frameCount) && frameCount > 1) return true;

    const trigger = String(
      meta.trigger ||
        raw.trigger ||
        ""
    )
      .trim()
      .toLowerCase();

    const reason = String(
      meta.reason ||
        raw.reason ||
        ""
    )
      .trim()
      .toLowerCase();

    if (
      trigger.includes("selected_frames") ||
      trigger.includes("multi_frame") ||
      trigger.includes("multiframe") ||
      reason.includes("multi_select") ||
      reason.includes("multi-frame") ||
      reason.includes("multiframe")
    ) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

function _singleFrameIdForAnchorMessage(message) {
  try {
    if (!message || _messageRepresentsMultiFrameAnalysis(message)) return "";

    const ids = _uniqCleanStrArray([
      String(message?.frame_id || "").trim(),
      ...(Array.isArray(message?.frame_ids) ? message.frame_ids : []),
      ..._extractFrameIdsFromMessage(message?.raw || message),
    ]).filter((fid) => fid !== "__LIVE__");

    return ids.length === 1 ? ids[0] : "";
  } catch {
    return "";
  }
}

function _findLatestSingleFrameAssistantMessage(
  messageList,
  frameId,
  { allowAssistantOnlyFallback = true } = {}
) {
  try {
    const fid = String(frameId || "").trim();
    if (!fid || fid === "__LIVE__") return null;

    const list = (Array.isArray(messageList) ? [...messageList] : [])
      .filter(Boolean)
      .sort((a, b) => {
        const ta = Number(a?.sortMs || a?.createdMs || 0);
        const tb = Number(b?.sortMs || b?.createdMs || 0);
        if (ta !== tb) return ta - tb;
        return Number(a?.__serverIndex || 0) - Number(b?.__serverIndex || 0);
      });

    const pairedCandidates = [];

    // Strongest path: pair every qualifying SINGLE-frame user analysis turn for
    // this frame with the first real Lexa response that follows it. Reanalysis
    // is intentionally eligible. Multi-frame turns are excluded by
    // _singleFrameIdForAnchorMessage(), and multi-frame Lexa responses are also
    // explicitly excluded below.
    for (let index = 0; index < list.length; index += 1) {
      const message = list[index];
      if (message?.role !== "user") continue;
      if (_singleFrameIdForAnchorMessage(message) !== fid) continue;

      for (let nextIndex = index + 1; nextIndex < list.length; nextIndex += 1) {
        const candidate = list[nextIndex];
        if (!candidate) continue;

        // A new user turn means this response window has ended.
        if (candidate.role === "user") break;

        if (candidate.role !== "assistant") continue;
        if (candidate.__hidden) continue;
        if (candidate?.raw?.__local) continue;
        if (!String(candidate?.text || "").trim()) continue;
        if (String(candidate?.id || "").startsWith("local_err_")) continue;
        if (_messageRepresentsMultiFrameAnalysis(candidate)) continue;

        pairedCandidates.push(candidate);
        break;
      }
    }

    if (pairedCandidates.length) {
      // The list is chronological, so the last qualifying single-frame pair is
      // the latest analysis/reanalysis for this exact frame_id.
      return pairedCandidates[pairedCandidates.length - 1];
    }

    // Compatibility fallback for older stored records where frame_id exists only
    // on the assistant response. While older pages still exist the caller can
    // defer this fallback and keep paging for the stronger user-turn pairing.
    if (!allowAssistantOnlyFallback) return null;

    let latestAssistantOnly = null;

    for (const message of list) {
      if (message?.role !== "assistant") continue;
      if (message?.__hidden) continue;
      if (message?.raw?.__local) continue;
      if (!String(message?.text || "").trim()) continue;
      if (String(message?.id || "").startsWith("local_err_")) continue;
      if (_messageRepresentsMultiFrameAnalysis(message)) continue;

      const singleFrameId = _singleFrameIdForAnchorMessage(message);
      if (singleFrameId === fid) {
        latestAssistantOnly = message;
      }
    }

    return latestAssistantOnly;
  } catch {
    return null;
  }
}

function _extractImageUrlsFromMessage(raw) {
  try {
    const r = raw && typeof raw === "object" ? raw : {};
    const attachment = r.attachment && typeof r.attachment === "object" ? r.attachment : {};
    const meta = r.meta && typeof r.meta === "object" ? r.meta : {};
    const metadata = r.metadata && typeof r.metadata === "object" ? r.metadata : {};

    const out = [];
    const pushUrl = (value) => {
      const s = typeof value === "string" ? value.trim() : "";
      if (s) out.push(s);
    };

    [
      r.image_url,
      r.imageUrl,
      r.file_url,
      r.fileUrl,
      attachment.image_url,
      attachment.imageUrl,
      attachment.file_url,
      attachment.fileUrl,
      meta.image_url,
      meta.imageUrl,
      metadata.image_url,
      metadata.imageUrl,
    ].forEach(pushUrl);

    [
      r.image_urls,
      r.imageUrls,
      r.file_urls,
      r.fileUrls,
      attachment.image_urls,
      attachment.imageUrls,
      attachment.file_urls,
      attachment.fileUrls,
      meta.image_urls,
      meta.imageUrls,
      metadata.image_urls,
      metadata.imageUrls,
    ].forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach(pushUrl);
    });

    [
      r.frames,
      attachment.frames,
      meta.frames,
      metadata.frames,
    ].forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((item) => {
        if (!item || typeof item !== "object") return;
        pushUrl(item.image_url || item.imageUrl || item.url || "");
      });
    });

    return Array.from(new Set(out.filter(Boolean)));
  } catch {
    return [];
  }
}

function _redactUrl(u) {
  const s = _trimStr(u);
  if (!s) return "";
  try {
    const url = new URL(s);
    const host = url.host;
    const path = url.pathname || "";
    const last = path.split("/").filter(Boolean).slice(-1)[0] || "";
    const tail = last.length > 28 ? `…${last.slice(-28)}` : last;
    return `${url.protocol}//${host}${path ? "/…/" : ""}${tail}`;
  } catch {
    if (s.startsWith("data:")) return `data:…(len=${s.length})`;
    return s.length > 60 ? `${s.slice(0, 30)}…(len=${s.length})` : s;
  }
}

// ---------------- JSON "lump" prevention helpers ----------------
function _safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function _looksLikeJsonObjectString(s) {
  try {
    const t = String(s || "").trim();
    if (!t) return false;
    if (!(t.startsWith("{") && t.endsWith("}"))) return false;

    // quick signal keys for our analyze payload
    if (t.includes('"assistant_reply"')) return true;
    if (t.includes('"assistantReply"')) return true;
    if (t.includes('"reply_markdown"')) return true;
    if (t.includes('"replyMarkdown"')) return true;
    if (t.includes('"frame_summary"')) return true;
    if (t.includes('"frameSummary"')) return true;

    return false;
  } catch {
    return false;
  }
}

function _extractAssistantTextFromAny(raw) {
  try {
    const r = raw || {};

    // Preferred explicit fields (future-proof)
    const direct =
      r.reply_markdown ||
      r.replyMarkdown ||
      r.assistant_reply ||
      r.assistantReply ||
      r.output_text ||
      r.outputText ||
      r?.meta?.reply_markdown ||
      r?.meta?.assistant_reply ||
      r?.metadata?.reply_markdown ||
      r?.metadata?.assistant_reply ||
      "";

    if (typeof direct === "string" && direct.trim()) return direct;

    // Sometimes backend stores payload as an object in message/text
    const msgObj =
      (typeof r.message === "object" && r.message) ||
      (typeof r.text === "object" && r.text) ||
      null;

    if (msgObj) {
      const t2 =
        msgObj.reply_markdown ||
        msgObj.replyMarkdown ||
        msgObj.assistant_reply ||
        msgObj.assistantReply ||
        msgObj.text ||
        msgObj.message ||
        "";
      if (typeof t2 === "string" && t2.trim()) return t2;
    }

    // Sometimes backend stores payload as a STRING in message/text
    const candidateStr =
      (typeof r.text === "string" && r.text) ||
      (typeof r.message === "string" && r.message) ||
      "";

    if (_looksLikeJsonObjectString(candidateStr)) {
      const parsed = _safeJsonParse(candidateStr);
      if (parsed && typeof parsed === "object") {
        const t3 =
          parsed.reply_markdown ||
          parsed.replyMarkdown ||
          parsed.assistant_reply ||
          parsed.assistantReply ||
          parsed.text ||
          parsed.message ||
          "";
        if (typeof t3 === "string" && t3.trim()) return t3;
      }
    }

    // Fallback: plain string
    return String(candidateStr || "");
  } catch {
    return "";
  }
}

// ---------------- Swallow assistant one-liner "status" messages (precision) ----------------
function _isSwallowableAssistantStatusLine(s) {
  try {
    const t = String(s || "").trim();
    if (!t) return false;

    // One-liner only (no paragraphs)
    if (t.includes("\n")) return false;

    // Keep tight length caps to avoid hiding real content
    if (t.length > 180) return false;

    const lower = t.toLowerCase();

    // Fingerprint: "Analyzed ... page(s) ... chunk(s)"
    if (/^analyzed\b/i.test(t) && lower.includes("page") && lower.includes("chunk")) return true;

    // Worker status/error one-liners only (avoid "workers' comp" etc.)
    if (/^call worker\b/i.test(lower)) return true;

    const startsWorker =
      lower.startsWith("worker") || lower.startsWith("worker:") || lower.startsWith("worker -");

    if (startsWorker) {
      if (
        /(not available|unavailable|timeout|timed out|failed|error|busy|capacity|overloaded|try again)/i.test(
          lower
        )
      ) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
}

function isProbablyImageUrl(url, mime, fileName) {
  try {
    const m = String(mime || "").toLowerCase().trim();
    if (m.startsWith("image/")) return true;

    const name = String(fileName || "").toLowerCase().trim();
    if (/\.(png|jpe?g|webp|gif)$/i.test(name)) return true;

    const raw = String(url || "").trim();
    if (!raw) return false;

    const base = raw.split("?")[0].split("#")[0];
    if (/\.(png|jpe?g|webp|gif)$/i.test(base)) return true;

    try {
      const u = new URL(raw, window.location.href);
      if (/\.(png|jpe?g|webp|gif)$/i.test(u.pathname || "")) return true;
    } catch {}

    return false;
  } catch {
    return false;
  }
}

// ---------------- Session snapshot helpers (NEW) ----------------
function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function _pickFirstNum(...vals) {
  for (const v of vals) {
    const n = _num(v);
    if (n > 0 || v === 0) return n;
  }
  return 0;
}

// We only need a "best effort" read. Parent snapshot shape may vary; we support common keys.
function _getRemainingSecondsFromSession(sess) {
  if (!sess || typeof sess !== "object") return null;

  const rs = _pickFirstNum(
    sess.remaining_seconds,
    sess.remainingSeconds,
    sess.remaining_secs,
    sess.remainingSecs,
    sess.seconds_remaining,
    sess.secondsRemaining,
    sess.time_remaining_seconds,
    sess.timeRemainingSeconds,
    sess?.timer?.remaining_seconds,
    sess?.timer?.remainingSeconds
  );

  // Some APIs send ms
  const rms = _pickFirstNum(
    sess.remaining_ms,
    sess.remainingMs,
    sess.time_remaining_ms,
    sess.timeRemainingMs,
    sess?.timer?.remaining_ms,
    sess?.timer?.remainingMs
  );

  if (rs > 0 || rs === 0) return rs;
  if (rms > 0 || rms === 0) return Math.floor(rms / 1000);

  // If parent only provides minutes, convert
  const rm = _pickFirstNum(
    sess.remaining_minutes,
    sess.remainingMinutes,
    sess.minutes_remaining,
    sess.minutesRemaining,
    sess?.timer?.remaining_minutes,
    sess?.timer?.remainingMinutes
  );
  if (rm > 0 || rm === 0) return Math.floor(rm * 60);

  return null;
}

function _isSessionEndedFromSnapshot(sess) {
  if (!sess || typeof sess !== "object") return false;

  const status = String(sess.status || sess.session_status || sess.state || "").toLowerCase();
  const endedFlag = !!(sess.ended || sess.isEnded || sess.session_ended || sess.sessionEnded);

  // Common terminal statuses
  if (endedFlag) return true;
  if (status.includes("ended") || status.includes("expired") || status.includes("complete")) return true;

  // If remaining seconds explicitly hits 0, treat as ended.
  const rs = _getRemainingSecondsFromSession(sess);
  if (rs === 0) return true;

  return false;
}

// ---------------- Manual upload completion detection (NEW) ----------------
function _isUploadComplete(att) {
  try {
    const p = Number(att?.progress);
    if (Number.isFinite(p)) return p >= 100;

    const status = String(att?.status || att?.state || att?.upload_status || "").toLowerCase().trim();
    if (status) {
      if (status.includes("complete")) return true;
      if (status.includes("completed")) return true;
      if (status.includes("done")) return true;
      if (status.includes("success")) return true;
      if (status === "uploaded") return true;
    }

    const fid = String(att?.frameId || att?.frame_id || "").trim();
    const fidsRaw = att?.frameIds || att?.frame_ids || att?.frames || [];
    const fids = Array.isArray(fidsRaw) ? fidsRaw.map((x) => String(x || "").trim()).filter(Boolean) : [];

    const fileUrl = String(att?.fileUrl || att?.file_url || "").trim();
    const fileUrlsRaw = att?.fileUrls || att?.file_urls || [];
    const fileUrls = Array.isArray(fileUrlsRaw)
      ? fileUrlsRaw.map((x) => String(x || "").trim()).filter(Boolean)
      : [];

    // best-effort: if we have a real result id/url and no explicit progress, treat as done
    return !!(fid || fids.length || fileUrl || fileUrls.length);
  } catch {
    return false;
  }
}


function formatIsoForVisionTranscriptFileName() {
  try {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(
      d.getHours()
    )}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
  } catch {
    return String(Date.now());
  }
}

function VisionNoticeDialog({ notice, onClose }) {
  if (!notice) return null;

  const dismissible = notice.dismissible !== false;

  const content = (
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) {
          onClose?.();
        }
      }}
      style={styles.noticeOverlay}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="vision-notice-title"
        aria-describedby="vision-notice-message"
        style={styles.noticeCard}
      >
        <div id="vision-notice-title" style={styles.noticeTitle}>
          {notice.title || "Vision Assist"}
        </div>

        <div id="vision-notice-message" style={styles.noticeMessage}>
          {notice.message || "Please try again."}
        </div>

        {notice.showSpinner ? (
          <div style={styles.noticeSpinnerRow}>
            <span style={styles.noticeSpinner} aria-hidden="true" />
            <span style={styles.noticeHint}>
              {notice.hint || "Please wait…"}
            </span>
          </div>
        ) : notice.hint ? (
          <div style={styles.noticeHint}>{notice.hint}</div>
        ) : null}

        {dismissible ? (
          <div style={styles.noticeActions}>
            <button
              type="button"
              onClick={onClose}
              autoFocus
              style={styles.noticeButton}
            >
              OK
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (typeof document === "undefined") return content;
  return createPortal(content, document.body);
}

const MessageBubble = memo(function MessageBubble({
  m,
  onSpeak,
  onCopy,
  onEdit,
  getResolvedFrameMedia,
  onImageError,
  highlighted = false,
}) {
  const isUser = m.role === "user";
  const bubbleStyle = isUser
    ? { ...styles.bubble, ...styles.bubbleUser }
    : {
        ...styles.bubble,
        ...styles.bubbleAssistant,
        ...(highlighted ? styles.bubbleAssistantAnchorHighlight : null),
      };

  const frameMedia = getResolvedFrameMedia
    ? getResolvedFrameMedia(m)
    : [];

  const visibleMedia = Array.isArray(frameMedia)
    ? frameMedia.slice(0, 5)
    : [];

  const multiScreen = visibleMedia.length > 1;
  const singleMedia = visibleMedia.length === 1 ? visibleMedia[0] : null;
  const singleUrl = String(singleMedia?.url || "").trim();

  const singleLooksLikeImage =
    !!singleUrl &&
    (!!singleMedia?.frameId ||
      isProbablyImageUrl(singleUrl, m.mime, m.file_name));

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        marginBottom: 16,
      }}
    >
      <div style={bubbleStyle}>
        <div style={styles.bubbleHeader}>
          <strong style={styles.bubbleName}>
            {m.role === "assistant" ? "Lexa" : m.role === "user" ? "You" : "Lexa"}
          </strong>

          <span style={styles.iconRow}>
            {m.role === "assistant" && (
              <>
                <FaVolumeUp
                  onClick={() =>
                    onSpeak(
                      m.text,
                      m.language ||
                        m?.raw?.language ||
                        m?.raw?.languageCode ||
                        m?.raw?.language_code ||
                        m?.raw?.responseLanguage ||
                        m?.raw?.response_language ||
                        m?.raw?.meta?.language ||
                        ""
                    )
                  }
                  style={styles.icon}
                  title="Read aloud"
                  aria-label="Read Lexa answer aloud"
                />
                <FaCopy onClick={() => onCopy(m.text)} style={styles.icon} title="Copy" />
              </>
            )}
            {m.role === "user" && (
              <>
                <FaEdit onClick={() => onEdit(m.text)} style={styles.icon} title="Edit" />
                <FaCopy onClick={() => onCopy(m.text)} style={styles.icon} title="Copy" />
              </>
            )}
          </span>
        </div>

        <div
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            userSelect: "text",
          }}
        >
          {isUser ? (
            <div className="asklawly-user-message-text">{m.text}</div>
          ) : (
            <AnswerRenderer text={m.text} />
          )}
        </div>

        {multiScreen ? (
          <div style={styles.screenStripBlock}>
            <div style={styles.screenStripLabel}>
              {visibleMedia.length} screens
            </div>
            <div style={styles.screenStrip}>
              {visibleMedia.map((item, idx) => {
                const fid = String(item?.frameId || "").trim();
                const url = String(item?.url || "").trim();
                const key = fid || url || `screen_${idx}`;

                return url ? (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.screenThumbLink}
                    title={`Open screen ${idx + 1}`}
                  >
                    <img
                      src={url}
                      alt={`Screen ${idx + 1}`}
                      style={styles.screenThumbImg}
                      onError={() => onImageError && onImageError(m, fid)}
                    />
                    <span style={styles.screenThumbIndex}>{idx + 1}</span>
                  </a>
                ) : (
                  <div key={key} style={styles.screenThumbPlaceholder}>
                    <span>Screen {idx + 1}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : singleMedia ? (
          <div style={styles.singleScreenBlock}>
            {singleUrl ? (
              singleLooksLikeImage ? (
                <img
                  src={singleUrl}
                  alt="Shared screen"
                  style={styles.messageScreenImage}
                  onError={() =>
                    onImageError &&
                    onImageError(m, String(singleMedia?.frameId || "").trim())
                  }
                />
              ) : (
                <a
                  href={singleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.fileLink}
                >
                  View file
                </a>
              )
            ) : (
              <div style={styles.screenLoading}>Loading screen…</div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default function VisionChatInterface({
  sessionId,
  session = null,

  assistMode = "B",
  launch = null,
  user = null,

  disabled = false,
  inputDisabled = false,
  inputDisabledReason = "",

  userType: userTypeProp = null,

  // ✅ NEW: Parent is the ONLY source of busy state
  isBusy = false, // boolean
  busyMode = "thinking", // "thinking" | "analyzing"

  // ✅ Frontend-only historical screenshot navigation request.
  // Shape from VisionAssistSession:
  //   { frameId: "<canonical frame_id>", requestId: <monotonic number> }
  // The chat resolves this ONLY to the LATEST single-frame Lexa response for
  // that exact frame_id. Reanalysis is eligible; multi-frame analyses never are.
  frameAnchorRequest = null,

  // ✅ NEW: Notify parent AFTER a manual upload is fully completed (results back; done)
  // Parent can use this to refresh thumbnails/filmstrip from backend.
  onManualUploadComplete = null,
}) {
  // --- Firebase auth user (no context) ---
  const [authUser, setAuthUser] = useState(() => getAuth()?.currentUser || null);
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setAuthUser(u || null));
    return () => unsub();
  }, []);

  const effectiveUser = useMemo(() => {
    const u = user || null;
    if (u?.uid) return u;
    if (authUser?.uid) {
      return {
        uid: authUser.uid || "",
        email: authUser.email || "",
        displayName: authUser.displayName || "",
        phoneNumber: authUser.phoneNumber || "",
      };
    }
    return null;
  }, [user, authUser]);

  const effectiveUid = effectiveUser?.uid || "";

  // ---------------- Centered in-app notices ----------------
  // Browser microphone permission prompts remain browser-owned. Every
  // VisionChatInterface notification/error is rendered inside AskLawly.
  const [notice, setNotice] = useState(null);

  const showVisionNotice = useCallback(
    (title, message, options = {}) => {
      setNotice({
        title: String(title || "Vision Assist"),
        message: String(message || "Please try again."),
        hint: String(options?.hint || ""),
        showSpinner: options?.showSpinner === true,
        dismissible: options?.dismissible !== false,
      });
    },
    []
  );

  const closeVisionNotice = useCallback(() => {
    setNotice(null);
  }, []);

  useEffect(() => {
    if (!notice || notice.dismissible === false) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setNotice(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [notice]);

  // ---------------- Vision Assist transcript PDF viewer ----------------
  // AttachmentMenu only forwards the utility action. This component owns the
  // transcript export + shared FileViewerModal lifecycle, matching ChatInterface.
  const [exportingTranscript, setExportingTranscript] = useState(false);
  const [transcriptRecord, setTranscriptRecord] = useState(null);
  const transcriptObjectUrlRef = useRef(null);

  const revokeTranscriptObjectUrl = useCallback(() => {
    const url = transcriptObjectUrlRef.current;
    transcriptObjectUrlRef.current = null;
    if (!url) return;

    try {
      window.URL.revokeObjectURL(url);
    } catch {}
  }, []);

  const closeTranscriptViewer = useCallback(() => {
    const url = transcriptObjectUrlRef.current;
    transcriptObjectUrlRef.current = null;
    setTranscriptRecord(null);

    if (url) {
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(url);
        } catch {}
      }, 100);
    }
  }, []);

  useEffect(() => {
    return () => {
      revokeTranscriptObjectUrl();
    };
  }, [revokeTranscriptObjectUrl]);


  // --- Session snapshot (optional; not required for messages feed) ---
  const [resolvedSession, setResolvedSession] = useState(null);

  useEffect(() => {
    let alive = true;
    let t = null;

    async function loadOnce() {
      if (session) return; // parent-supplied snapshot is authoritative
      if (!sessionId) return;
      if (!effectiveUid) return;

      try {
        const s = await getVisionSession(sessionId);
        if (!alive) return;
        setResolvedSession(s || null);
      } catch {
        if (!alive) return;
        setResolvedSession(null);
      }
    }

    loadOnce();

    // ✅ Poll session ONLY when parent did NOT pass `session`
    if (!session && sessionId && effectiveUid) {
      t = setInterval(loadOnce, 2000);
    }

    return () => {
      alive = false;
      if (t) clearInterval(t);
    };
  }, [sessionId, effectiveUid, session]);

  const effectiveSession = useMemo(() => {
    return session || resolvedSession || null;
  }, [session, resolvedSession]);


  const handleOpenVisionTranscript = useCallback(async () => {
    if (exportingTranscript) return;

    if (!sessionId) {
      showVisionNotice(
        "No transcript yet",
        "Start a Vision Assist session before opening the transcript."
      );
      return;
    }

    setExportingTranscript(true);
    showVisionNotice(
      "Preparing transcript…",
      "Generating your PDF Vision Assist transcript.",
      {
        hint: "This can take a moment for a long session.",
        showSpinner: true,
        dismissible: false,
      }
    );

    try {
      const blob = await exportVisionTranscriptPdf(
        sessionId,
        {
          download: false,
        }
      );

      if (!blob || blob.size <= 0) {
        throw new Error("The generated transcript PDF was empty.");
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const fileName = `vision_transcript_${sessionId}_${formatIsoForVisionTranscriptFileName()}.pdf`;

      const rawTitle = String(
        effectiveSession?.title ||
          effectiveSession?.session_name ||
          effectiveSession?.goal ||
          ""
      )
        .replace(/\s+/g, " ")
        .trim();

      const title = rawTitle
        ? `${rawTitle} — Vision Assist transcript`
        : "Vision Assist transcript";

      revokeTranscriptObjectUrl();
      transcriptObjectUrlRef.current = objectUrl;

      setTranscriptRecord({
        title,
        subject: title,
        filename: fileName,
        name: fileName,
        format: "pdf",
        fileFormat: "pdf",
        contentType: "application/pdf",
        mimeType: "application/pdf",
        viewerUrl: objectUrl,
        url: objectUrl,
        downloadUrl: objectUrl,
        pdfUrl: objectUrl,
        pdfUrlInline: objectUrl,
        urls: { pdf: objectUrl },
        allowDownload: true,
        linkAllowDownload: true,
        createdAt: new Date().toISOString(),
      });

      setNotice(null);
    } catch (error) {
      try {
        console.warn(
          "[VisionChatInterface] transcript PDF preview failed:",
          error?.message || error
        );
      } catch {}

      showVisionNotice(
        "Transcript failed",
        "Unable to prepare the transcript right now. Please try again."
      );
    } finally {
      setExportingTranscript(false);
    }
  }, [
    effectiveSession,
    exportingTranscript,
    revokeTranscriptObjectUrl,
    sessionId,
    showVisionNotice,
  ]);

  // ✅ time-based UI policy derived from parent snapshot/session
  const remainingSeconds = useMemo(() => {
    const v = _getRemainingSecondsFromSession(effectiveSession);
    return typeof v === "number" ? v : null;
  }, [effectiveSession]);

  // ✅ When <= 60 seconds remain => hide attachments + ignore attachments
  const inFinalMinute = useMemo(() => {
    return typeof remainingSeconds === "number" ? remainingSeconds <= 60 : false;
  }, [remainingSeconds]);

  // ✅ Session ended/expired (manual end OR 00:00 OR browser chose disable) => disable input
  const sessionEnded = useMemo(() => {
    return _isSessionEndedFromSnapshot(effectiveSession);
  }, [effectiveSession]);

  // NOTE: hardDisabled remains as-is (external disables). Busy is handled separately (send lock + indicator).
  // ✅ Also disable if session ended (covers manual end / 00:00 / other terminal snapshot states)
  const hardDisabled = !!(disabled || inputDisabled || sessionEnded);

  // ✅ Parent-busy is the ONLY busy state for this component.
  const parentBusy = !!isBusy;

  const uiBusyMode = useMemo(() => {
    if (!parentBusy) return "";
    return String(busyMode || "").toLowerCase().includes("analy") ? "analyzing" : "thinking";
  }, [parentBusy, busyMode]);

  // ---------------- Busy bubble delay (after assistant message arrives) ----------------
  const [busyDelayUntilMs, setBusyDelayUntilMs] = useState(0);
  const busyDelayTimerRef = useRef(null);
  const lastAssistantSeenIdRef = useRef("");
  const delayArmedForBusyCycleRef = useRef(false);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (busyDelayTimerRef.current) {
        clearTimeout(busyDelayTimerRef.current);
        busyDelayTimerRef.current = null;
      }
    };
  }, []);

  // Reset delay gating when parent is no longer busy (end of "busy cycle")
  useEffect(() => {
    if (parentBusy) return;

    delayArmedForBusyCycleRef.current = false;

    if (busyDelayTimerRef.current) {
      clearTimeout(busyDelayTimerRef.current);
      busyDelayTimerRef.current = null;
    }
    setBusyDelayUntilMs(0);
  }, [parentBusy]);

  // --- State ---
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Vision history follows the same contract as ChatInterface:
  // newest 40 first, then ↑ loads the previous 40 using next_cursor.
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyNextCursor, setHistoryNextCursor] = useState("");
  const [historyLoadingPrevious, setHistoryLoadingPrevious] = useState(false);
  const historyLoadingPreviousRef = useRef(false);
  const historyHasMoreRef = useRef(false);
  const historyNextCursorRef = useRef("");
  const historyInitializedRef = useRef(false);
  const historyPrependScrollRestoreRef = useRef(null);

  const setHistoryCursorState = useCallback((hasMore, nextCursor) => {
    const nextHasMore = !!hasMore;
    const next = String(nextCursor || "").trim();
    historyHasMoreRef.current = nextHasMore;
    historyNextCursorRef.current = next;
    setHistoryHasMore(nextHasMore);
    setHistoryNextCursor(next);
  }, []);

  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const textareaRef = useRef(null);

  const resetTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    try {
      el.style.height = `${TEXTAREA_DEFAULT_HEIGHT}px`;
      el.style.overflowY = "hidden";
    } catch {}
  }, []);

  const sizeTextareaToContent = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    try {
      el.style.height = `${TEXTAREA_DEFAULT_HEIGHT}px`;
      el.style.overflowY = "hidden";

      if (!String(el.value || "").length) return;

      const neededHeight = Math.max(
        TEXTAREA_DEFAULT_HEIGHT,
        el.scrollHeight || 0
      );

      const shouldGrow =
        neededHeight >
        TEXTAREA_DEFAULT_HEIGHT + TEXTAREA_GROW_TOLERANCE_PX;

      const nextHeight = shouldGrow
        ? Math.min(neededHeight, TEXTAREA_MAX_HEIGHT)
        : TEXTAREA_DEFAULT_HEIGHT;

      el.style.height = `${nextHeight}px`;
      el.style.overflowY =
        neededHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
    } catch (error) {
      try {
        console.warn(
          "[VisionChatInterface] textarea auto-grow failed:",
          error?.message || error
        );
      } catch {}
    }
  }, []);

  useLayoutEffect(() => {
    sizeTextareaToContent();
  }, [input, sizeTextareaToContent]);


  // ✅ Ref-based send lock to prevent accidental double-send (Enter + click, key repeat, etc.)
  const sendLockRef = useRef(false);

  // ✅ Pending-local registry for robust dedupe of optimistic user message
  // Map<clientCreatedAt:number, { localId, textSig, textNorm, frameId, baselineServer }>
  const pendingLocalRef = useRef(new Map());

  // ---------------- Activity refs for adaptive polling ----------------
  const lastActivityMsRef = useRef(Date.now());
  const parentBusyRef = useRef(parentBusy);
  useEffect(() => {
    parentBusyRef.current = parentBusy;
  }, [parentBusy]);

  const inputLenRef = useRef(0);
  useEffect(() => {
    inputLenRef.current = (input || "").length;
  }, [input]);

  // Attachments
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const plusButtonRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [draftAttachment, setDraftAttachment] = useState(null);

  // ✅ NEW: Dedup + sequencing for manual upload completion notify
  const manualUploadSeqRef = useRef(0);
  const lastManualUploadKeyRef = useRef("");

  // ----------------------------
  // ✅ Voice (NEW): MediaRecorder + backend STT (direct route)
  // ----------------------------
  const [recording, setRecording] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [voiceText, setVoiceText] = useState("");

  // ✅ NEW: Transcribe state (spinner/guard)
  const [isTranscribing, setIsTranscribing] = useState(false);

  // ✅ NEW: "accept pipeline" busy (covers stop->finalize->upload->transcribe so spinner shows on all devices)
  const [isAcceptingVoice, setIsAcceptingVoice] = useState(false);

  // ✅ Voice waveform UI (input area only)
  const [waveformData, setWaveformData] = useState([]);
  const isFlatRef = useRef(true);

  // ✅ Robust tap guard (prevents double-trigger on mobile)
  const lastVoiceActionAtRef = useRef(0);
  const safeVoiceAction = (fn) => {
    const now = Date.now();
    if (now - (lastVoiceActionAtRef.current || 0) < 250) return;
    lastVoiceActionAtRef.current = now;
    fn?.();
  };

  // ✅ Guard to prevent repeated authentication notices
  const lastAuthAlertAtRef = useRef(0);
  const showAuthAlertOnce = (msg) => {
    const now = Date.now();
    if (now - (lastAuthAlertAtRef.current || 0) < 1500) return false;
    lastAuthAlertAtRef.current = now;
    showVisionNotice("Voice unavailable", msg);
    return true;
  };

  // ✅ Inject keyframes ONCE (prevents spinner "flicker/jitter" caused by frequent re-renders)
  useEffect(() => {
    const STYLE_ID = "asklawly-visionchat-keyframes";
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.type = "text/css";
    style.textContent = `
@keyframes asklawlySendLift {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
@keyframes asklawlySpin360 {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes chatSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
    document.head.appendChild(style);
  }, []);

  // ✅ Clear 360° spinner (SVG) — Safari/iOS safe
  const Spinner = ({ size = 16 }) => (
    <span
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        willChange: "transform",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{
          display: "block",
          transformBox: "fill-box",
          transformOrigin: "50% 50%",
          WebkitTransformOrigin: "50% 50%",
          animation: "asklawlySpin360 0.85s linear infinite",
          WebkitAnimation: "asklawlySpin360 0.85s linear infinite",
        }}
      >
        <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="3" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          fill="none"
          stroke="#1E2A78"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );

  // MediaRecorder refs/state
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);

  const recordedBlobRef = useRef(null);
  const recordedMimeRef = useRef("");
  const pendingAcceptRef = useRef(false); // if user taps ✓ while recording, we stop and then transcribe

  // ✅ Minimums to avoid "empty_transcript" from too-short / too-tiny recordings
  const MIN_RECORD_MS = 1000; // 1.0s minimum
  const MIN_BLOB_BYTES = 15000; // ~15KB minimum (tuned for opus/webm)
  const recordStartAtRef = useRef(0);

  // Ensure stop() flushes last chunk before transcribe (prevents empty_transcript)
  const stopPromiseRef = useRef(null);
  const ensureRecorderStopped = () => {
    try {
      const mr = mediaRecorderRef.current;
      if (!mr) return Promise.resolve();
      if (mr.state === "inactive") return Promise.resolve();
      if (stopPromiseRef.current) return stopPromiseRef.current;

      stopPromiseRef.current = new Promise((resolve) => {
        const prevOnStop = mr.onstop;
        mr.onstop = (ev) => {
          try {
            prevOnStop?.(ev);
          } catch {}
          resolve();
          stopPromiseRef.current = null;
        };

        try {
          mr.requestData?.();
        } catch {}

        try {
          mr.stop();
        } catch {
          resolve();
          stopPromiseRef.current = null;
        }
      });

      return stopPromiseRef.current;
    } catch {
      return Promise.resolve();
    }
  };

  const pickBestMimeType = () => {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg"];
    for (const t of candidates) {
      try {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
      } catch {}
    }
    return "";
  };

  const stopTracks = (stream) => {
    try {
      stream?.getTracks?.().forEach((t) => {
        try {
          t.stop();
        } catch {}
      });
    } catch {}
  };

  const resetVoiceState = () => {
    setRecording(false);
    setVoiceReady(false);
    setVoiceText("");
    setWaveformData([]);
    isFlatRef.current = true;

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    pendingAcceptRef.current = false;
    stopPromiseRef.current = null;
    recordStartAtRef.current = 0;

    // ✅ clear busy flags
    setIsAcceptingVoice(false);
  };

  const getIdTokenSafe = async () => {
    try {
      const u = authUser || getAuth()?.currentUser || null;
      if (!u?.getIdToken) return null;
      return await u.getIdToken();
    } catch (e) {
      try {
        console.warn("[VisionChatInterface][STT] getIdToken failed:", e);
      } catch {}
      return null;
    }
  };

  // ✅ Text helpers (keeps behavior consistent with ChatInput)
  const normalizeSpaces = (s = "") => String(s || "").replace(/\s+/g, " ").trim();

  const needsSpaceBetween = (left = "", right = "") => {
    if (!left || !right) return false;
    const l = left.slice(-1);
    const r = right[0];
    if (/^[,.;:!?)]$/.test(r)) return false;
    if (!/\s/.test(l) && !/[([{-]/.test(l)) return true;
    return false;
  };

  const appendWithDeoverlap = (base = "", next = "") => {
    let a = normalizeSpaces(base);
    let b = normalizeSpaces(next);
    if (!b) return a;

    if (a.toLowerCase().endsWith(b.toLowerCase())) return a;

    const maxCheck = Math.min(50, a.length, b.length);
    for (let k = maxCheck; k > 0; k--) {
      if (a.slice(-k).toLowerCase() === b.slice(0, k).toLowerCase()) {
        b = b.slice(k);
        break;
      }
    }

    if (!b) return a;
    const glue = needsSpaceBetween(a, b) ? " " : "";
    return (a + glue + b).trim();
  };

  const transcribeAndAppend = async (blob, mimeType) => {
    if (!HOST) {
      showVisionNotice("Voice unavailable", "Voice transcription is not configured right now.");
      setIsAcceptingVoice(false);
      return;
    }

    if (!sessionId) {
      showVisionNotice("Voice unavailable", "This Vision Assist session is not ready for voice transcription.");
      setIsAcceptingVoice(false);
      return;
    }

    if (!blob || blob.size <= 0) {
      showVisionNotice("No audio captured", "Please try again and speak a little longer.");
      setIsAcceptingVoice(false);
      return;
    }

    if (blob.size < MIN_BLOB_BYTES) {
      showVisionNotice("Audio too short", "Please speak a bit longer and try again.");
      setIsAcceptingVoice(false);
      return;
    }

    const token = await getIdTokenSafe();
    if (!token) {
      showAuthAlertOnce("Please log in again to use voice input.");
      setIsAcceptingVoice(false);
      return;
    }

    setIsTranscribing(true);
    setVoiceText("");

    try {
      const ext = (mimeType || "").includes("mp4")
        ? "m4a"
        : (mimeType || "").includes("mpeg")
        ? "mp3"
        : "webm";

      const file = new File([blob], `speech.${ext}`, { type: mimeType || "audio/webm" });

      const fd = new FormData();
      fd.append("file", file);
      fd.append("language", "auto");

      // best-effort metadata (same pattern as ChatInput; safe if backend ignores)
      fd.append("sessionId", sessionId || "");
      fd.append("sessionKind", "vision_assist");
      fd.append("userEmail", effectiveUser?.email || "");
      fd.append("userType", userTypeProp || effectiveSession?.user_type || effectiveSession?.owner_type || "individual");

      // ✅ Direct STT route (same as ChatInput pattern)
      const url = `${HOST}/stt/transcribe`;

      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        const reason = data?.reason || data?.error || `stt_failed_${res.status}`;
        throw new Error(reason);
      }

      const text = (data?.text || data?.transcript || "").toString().trim();
      if (!text) throw new Error("empty_transcript");

      setVoiceText(text);

      // ✅ ACCEPT INTO INPUT (NO AUTO-SEND)
      setInput((prev) => {
        const left = normalizeSpaces(prev || "");
        if (!left) return text;
        return appendWithDeoverlap(left, text);
      });

      requestAnimationFrame(() => {
        try {
          const el = textareaRef.current;
          el?.focus?.();
          if (el && typeof el.selectionStart === "number") {
            const end = (el.value || "").length;
            el.selectionStart = end;
            el.selectionEnd = end;
          }
        } catch {}
      });

      // Reset voice UI back to textarea
      resetVoiceState();
    } catch (err) {
      try {
        console.error("[VisionChatInterface][STT] Transcribe failed:", err);
      } catch {}
      showVisionNotice("Voice transcription failed", "We couldn’t transcribe that recording. Please try again.");
      setVoiceReady(true);
    } finally {
      setIsTranscribing(false);
      setIsAcceptingVoice(false);
    }
  };

  const startRecording = async () => {
    if (parentBusy) return;
    if (hardDisabled) return;
    if (isTranscribing || isAcceptingVoice) return;
    if (recording) return;

    if (!navigator?.mediaDevices?.getUserMedia) {
      showVisionNotice("Microphone unavailable", "Microphone recording is not supported on this device or browser.");
      return;
    }

    // must be logged in for STT auth
    if (!authUser && !getAuth()?.currentUser) {
      showAuthAlertOnce("Please log in again to use voice input.");
      return;
    }

    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedMimeRef.current = "";
    setVoiceReady(false);
    setVoiceText("");
    setWaveformData([]);
    isFlatRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const mimeType = pickBestMimeType();
      recordedMimeRef.current = mimeType || "";

      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e?.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      mr.onstart = () => {
        recordStartAtRef.current = Date.now();
        setRecording(true);
        isFlatRef.current = false;
      };

      mr.onstop = () => {
        setRecording(false);
        isFlatRef.current = true;

        const chunks = recordedChunksRef.current || [];
        const type = recordedMimeRef.current || chunks?.[0]?.type || "audio/webm";
        const blob = new Blob(chunks, { type });
        recordedBlobRef.current = blob;

        // Stop mic hardware immediately after recording
        try {
          stopTracks(streamRef.current);
        } catch {}
        streamRef.current = null;

        const durMs = recordStartAtRef.current ? Date.now() - recordStartAtRef.current : 0;

        if (!blob || blob.size <= 0) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVisionNotice("No audio captured", "Please try again and speak a little longer.");
          setIsAcceptingVoice(false);
          return;
        }

        if (durMs > 0 && durMs < MIN_RECORD_MS) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVisionNotice("Recording too short", "Please speak for at least 1 second and try again.");
          setIsAcceptingVoice(false);
          return;
        }

        if (blob.size < MIN_BLOB_BYTES) {
          setVoiceReady(false);
          recordedBlobRef.current = null;
          recordedChunksRef.current = [];
          showVisionNotice("Audio too short", "Please speak a bit longer and try again.");
          setIsAcceptingVoice(false);
          return;
        }

        setVoiceReady(true);

        // If user hit ✓ while still recording, transcribe immediately now
        if (pendingAcceptRef.current) {
          pendingAcceptRef.current = false;
          transcribeAndAppend(blob, type);
        }
      };

      // timeslice keeps data flowing; helps some mobile browsers
      mr.start(250);

      lastActivityMsRef.current = Date.now();
    } catch (err) {
      try {
        console.warn("[VisionChatInterface][STT] Mic start error:", err);
      } catch {}
      resetVoiceState();
      showVisionNotice("Microphone access unavailable", "Please allow microphone permission and try again.");
    }
  };

  const stopRecording = async () => {
    try {
      try {
        mediaRecorderRef.current?.requestData?.();
      } catch {}
      await new Promise((r) => setTimeout(r, 180));
      await ensureRecorderStopped();
    } catch (e) {
      try {
        console.warn("[VisionChatInterface][STT] stopRecording failed:", e);
      } catch {}
    }
  };

  const cancelVoice = () => {
    safeVoiceAction(() => {
      if (isTranscribing) return;

      try {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
          mr.ondataavailable = null;
          mr.onstop = null;
          mr.stop();
        }
      } catch {}

      mediaRecorderRef.current = null;

      try {
        stopTracks(streamRef.current);
      } catch {}
      streamRef.current = null;

      setIsAcceptingVoice(false);
      setIsTranscribing(false);
      resetVoiceState();

      lastActivityMsRef.current = Date.now();
    });
  };

  const handleVoiceInput = () => {
    safeVoiceAction(() => {
      if (parentBusy) return;
      if (hardDisabled) return;
      if (isUploading) return;
      if (isSending) return;

      if (!authUser && !getAuth()?.currentUser) {
        showAuthAlertOnce("Please log in again to use voice input.");
        return;
      }

      if (recording) return;
      if (isTranscribing || isAcceptingVoice) return;

      if (voiceReady) resetVoiceState();
      startRecording();
    });
  };

  const acceptVoice = () => {
    safeVoiceAction(async () => {
      if (parentBusy) return;
      if (hardDisabled) return;
      if (isTranscribing || isAcceptingVoice) return;

      if (!authUser && !getAuth()?.currentUser) {
        showAuthAlertOnce("Please log in again to use voice input.");
        return;
      }

      // ✅ Start spinner immediately (covers stop -> onstop -> upload)
      setIsAcceptingVoice(true);

      try {
        if (recording) {
          const durMs = recordStartAtRef.current ? Date.now() - recordStartAtRef.current : 0;
          if (durMs > 0 && durMs < MIN_RECORD_MS) {
            showVisionNotice("Recording too short", "Please speak for at least 1 second.");
            setIsAcceptingVoice(false);
            return;
          }

          pendingAcceptRef.current = true;
          await stopRecording();
          return;
        }

        if (voiceReady && recordedBlobRef.current) {
          await transcribeAndAppend(
            recordedBlobRef.current,
            recordedMimeRef.current || recordedBlobRef.current.type
          );
          return;
        }

        setIsAcceptingVoice(false);
      } catch (e) {
        try {
          console.warn("[VisionChatInterface][STT] acceptVoice failed:", e);
        } catch {}
        setIsAcceptingVoice(false);
      }
    });
  };

  // ✅ Wave animation ONLY while recording or when blob ready.
  // ❗️Do NOT run interval during isTranscribing / isAcceptingVoice — prevents spinner wiggle on mobile.
  useEffect(() => {
    if (isTranscribing || isAcceptingVoice) return;
    if (!recording && !voiceReady) return;

    const interval = setInterval(() => {
      const sample = isFlatRef.current ? 2 : Math.random() * 20;
      setWaveformData((data) => [...data.slice(-100), sample]);
    }, 60);

    return () => clearInterval(interval);
  }, [recording, voiceReady, isTranscribing, isAcceptingVoice]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        mediaRecorderRef.current?.stop?.();
      } catch {}
      mediaRecorderRef.current = null;

      try {
        stopTracks(streamRef.current);
      } catch {}
      streamRef.current = null;

      recordedChunksRef.current = [];
      recordedBlobRef.current = null;
      recordedMimeRef.current = "";
      pendingAcceptRef.current = false;
      stopPromiseRef.current = null;
      recordStartAtRef.current = 0;

      try {
        setIsAcceptingVoice(false);
        setIsTranscribing(false);
      } catch {}
    };
  }, []);

  // ---------------- Refresh-safe frame URL cache ----------------
  const [frameUrlMap, setFrameUrlMap] = useState({});
  const frameUrlMapRef = useRef({});
  useEffect(() => {
    frameUrlMapRef.current = frameUrlMap || {};
  }, [frameUrlMap]);

  const inflightFrameRef = useRef(new Set());
  const frameRetryRef = useRef({});

  // Signed/expiring image_url may be expired later. If frame_id exists, re-sign and ignore bad direct urls.
  const badDirectFrameRef = useRef(new Set()); // stores frameIds whose direct url failed

  const lastServerCreatedMsRef = useRef(0); // server-aligned max (uses sortMs)
  const prevSessionKeyRef = useRef("");
  const nextLocalSortMsRef = useRef(0);

  const ensureFrameUrl = useCallback(
    async (frameId) => {
      const fid = String(frameId || "").trim();
      if (!fid) return;

      if (!sessionId || !effectiveUid) return;

      if (frameUrlMapRef.current?.[fid]) return;

      if (inflightFrameRef.current.has(fid)) return;
      inflightFrameRef.current.add(fid);

      try {
        const res = await getVisionFrameUrl(sessionId, fid);
        const url = String(res?.image_url || "").trim();
        if (url) {
          setFrameUrlMap((prev) => {
            const cur = prev && typeof prev === "object" ? prev : {};
            if (cur[fid]) return cur;
            return { ...cur, [fid]: url };
          });
        }
      } catch {
        // swallow
      } finally {
        inflightFrameRef.current.delete(fid);
      }
    },
    [sessionId, effectiveUid]
  );

  const getResolvedFrameMedia = useCallback(
    (m) => {
      try {
        const frameIds = _uniqCleanStrArray([
          String(m?.frame_id || "").trim(),
          ...(Array.isArray(m?.frame_ids) ? m.frame_ids : []),
        ]).slice(0, 5);

        const directUrls = _uniqCleanStrArray([
          String(m?.image_url || "").trim(),
          ...(Array.isArray(m?.image_urls) ? m.image_urls : []),
        ]).slice(0, 5);

        if (frameIds.length) {
          return frameIds.map((fid, idx) => {
            const cached = String(frameUrlMapRef.current?.[fid] || "").trim();

            if (!cached) {
              ensureFrameUrl(fid);
            }

            const direct = String(directUrls[idx] || "").trim();
            const directAllowed = !badDirectFrameRef.current?.has(fid);

            return {
              frameId: fid,
              url: cached || (directAllowed ? direct : "") || "",
            };
          });
        }

        return directUrls.map((url, idx) => ({
          frameId: "",
          url,
          index: idx,
        }));
      } catch {
        return [];
      }
    },
    [ensureFrameUrl]
  );

  const handleImageError = useCallback(
    (m, frameIdOverride = "") => {
      try {
        const fid =
          String(frameIdOverride || "").trim() ||
          String(m?.frame_id || "").trim();

        if (!fid) return;

        try {
          badDirectFrameRef.current.add(fid);
        } catch {}

        const retryMap = frameRetryRef.current || {};
        const count = Number(retryMap[fid] || 0);
        if (count >= 2) return;

        retryMap[fid] = count + 1;
        frameRetryRef.current = retryMap;

        setFrameUrlMap((prev) => {
          const cur = prev && typeof prev === "object" ? prev : {};
          if (!cur[fid]) return cur;
          const next = { ...cur };
          delete next[fid];
          return next;
        });

        ensureFrameUrl(fid);
      } catch {}
    },
    [ensureFrameUrl]
  );

  // ✅ If parent disables input, stop voice immediately.
  // The transcript utility menu is independent from chat-send availability.
  useEffect(() => {
    if (!hardDisabled) return;

    // stop voice & reset all flags
    try {
      mediaRecorderRef.current?.stop?.();
    } catch {}
    mediaRecorderRef.current = null;

    try {
      stopTracks(streamRef.current);
    } catch {}
    streamRef.current = null;

    setRecording(false);
    setVoiceReady(false);
    setVoiceText("");
    setWaveformData([]);
    isFlatRef.current = true;

    setIsAcceptingVoice(false);
    setIsTranscribing(false);
  }, [hardDisabled]);

  // ✅ When parent is busy, immediately stop voice. Transcript remains available.
  useEffect(() => {
    if (!parentBusy) return;

    try {
      mediaRecorderRef.current?.stop?.();
    } catch {}
    mediaRecorderRef.current = null;

    try {
      stopTracks(streamRef.current);
    } catch {}
    streamRef.current = null;

    setRecording(false);
    setVoiceReady(false);
    setVoiceText("");
    setWaveformData([]);
    isFlatRef.current = true;

    setIsAcceptingVoice(false);
    setIsTranscribing(false);
  }, [parentBusy]);

  // Manual attachments stay disabled. Final-minute timing does not hide the
  // plus utility because Chat transcript remains useful throughout the session.
  useEffect(() => {
    if (!inFinalMinute) return;
    if (VISION_MANUAL_ATTACHMENTS_ENABLED) {
      setDraftAttachment(null);
    }
  }, [inFinalMinute]);

  const canSend = useMemo(() => {
    if (hardDisabled) return false;
    if (parentBusy) return false; // ✅ parent busy => send disabled
    if (!effectiveUid) return false;
    if (!sessionId) return false;
    if (isUploading) return false;
    if (isSending) return false;

    const hasText = !!input.trim();

    // Vision Assist chat is intentionally text-only. Screen-share analysis is
    // driven by the separate parent/frame pipeline, not by this composer.
    if (!VISION_MANUAL_ATTACHMENTS_ENABLED) return hasText;

    // Legacy dormant behavior if manual attachments are deliberately re-enabled.
    if (inFinalMinute) return hasText;

    const hasFrameId = !!String(draftAttachment?.frameId || draftAttachment?.frame_id || "").trim();
    const hasFrameIds = Array.isArray(draftAttachment?.frameIds) && draftAttachment.frameIds.length > 0;
    const hasFileUrl = !!draftAttachment?.fileUrl;
    const hasFileUrls = Array.isArray(draftAttachment?.fileUrls) && draftAttachment.fileUrls.length > 0;

    return hasText || hasFrameId || hasFrameIds || hasFileUrl || hasFileUrls;
  }, [
    hardDisabled,
    parentBusy,
    effectiveUid,
    sessionId,
    isUploading,
    isSending,
    input,
    draftAttachment,
    inFinalMinute,
  ]);

  // ---------------- Response anchoring + manual-friendly scroll ----------------
  const logRef = useRef(null);
  const messageNodeMapRef = useRef(new Map());
  const busyBubbleRef = useRef(null);

  const atBottomRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const forceScrollOnceRef = useRef(false);
  const scrollIntentRef = useRef(null);
  const [feedSig, setFeedSig] = useState("");

  // Historical thumbnail -> Lexa response navigation.
  // This is deliberately independent from the live response-anchor lifecycle.
  const [highlightedHistoryMessageId, setHighlightedHistoryMessageId] = useState("");
  const historyHighlightTimerRef = useRef(null);
  const pendingFrameAnchorRef = useRef(null);
  const handledFrameAnchorRequestIdRef = useRef("");
  const frameAnchorGenerationRef = useRef(0);
  const [frameAnchorRequestTick, setFrameAnchorRequestTick] = useState(0);

  const clearHistoryHighlight = useCallback(() => {
    if (historyHighlightTimerRef.current) {
      clearTimeout(historyHighlightTimerRef.current);
      historyHighlightTimerRef.current = null;
    }
    setHighlightedHistoryMessageId("");
  }, []);

  const highlightHistoricalMessage = useCallback((messageId) => {
    const id = String(messageId || "").trim();
    if (!id) return;

    if (historyHighlightTimerRef.current) {
      clearTimeout(historyHighlightTimerRef.current);
      historyHighlightTimerRef.current = null;
    }

    setHighlightedHistoryMessageId(id);

    historyHighlightTimerRef.current = setTimeout(() => {
      setHighlightedHistoryMessageId((current) => (current === id ? "" : current));
      historyHighlightTimerRef.current = null;
    }, 1800);
  }, []);

  useEffect(() => {
    return () => {
      if (historyHighlightTimerRef.current) {
        clearTimeout(historyHighlightTimerRef.current);
        historyHighlightTimerRef.current = null;
      }
    };
  }, []);

  // Response-anchor contract:
  // 1) The user's newly sent turn is shown/anchored immediately.
  // 2) As soon as parent busy starts on ANY route (chat or analyze), move the
  //    viewport to Lexa's Thinking/Analyzing bubble.
  // 3) As soon as the real Lexa assistant message arrives, move the anchor from
  //    the busy bubble to that assistant message and keep the viewport there.
  // 4) Never chase the bottom while that response cycle is anchored.
  const responseAnchorActiveRef = useRef(false);
  const responseAnchorIdRef = useRef("");
  const awaitingBusyUserAnchorRef = useRef(false);
  const busyBaselineUserIdRef = useRef("");
  const previousParentBusyForAnchorRef = useRef(false);

  const registerMessageNode = useCallback((messageId, node) => {
    const id = String(messageId || "").trim();
    if (!id) return;

    if (node) messageNodeMapRef.current.set(id, node);
    else messageNodeMapRef.current.delete(id);
  }, []);

  const getDistanceFromLatest = () => {
    const el = logRef.current;
    if (!el) return 0;

    const scrollTop = el.scrollTop || 0;
    const clientH = el.clientHeight || 0;
    const scrollH = el.scrollHeight || 0;
    return Math.max(0, scrollH - (scrollTop + clientH));
  };

  const syncScrollNavigationState = () => {
    const distance = getDistanceFromLatest();
    atBottomRef.current = distance <= 8;
    setShowJumpToLatest(
      distance > VISION_JUMP_TO_LATEST_DISTANCE_PX
    );
  };

  const scrollToBottom = (behavior = "auto") => {
    const el = logRef.current;
    if (!el) return;

    try {
      if (behavior === "smooth" && typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    } catch {}

    atBottomRef.current = true;
    setShowJumpToLatest(false);
  };

  const scrollNodeIntoLog = useCallback((node, behavior = "smooth", align = "start") => {
    const el = logRef.current;
    if (!el || !node) return false;

    try {
      const logRect = el.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();

      let target =
        el.scrollTop +
        (nodeRect.top - logRect.top) -
        10;

      if (align === "end") {
        target =
          el.scrollTop +
          (nodeRect.bottom - logRect.bottom) +
          10;
      }

      const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
      const clamped = Math.max(0, Math.min(maxTop, target));

      if (typeof el.scrollTo === "function") {
        el.scrollTo({ top: clamped, behavior });
      } else {
        el.scrollTop = clamped;
      }

      atBottomRef.current = false;
      setShowJumpToLatest(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  const scrollToMessageAnchor = useCallback(
    (messageId, behavior = "smooth") => {
      const id = String(messageId || "").trim();
      if (!id) return false;
      const node = messageNodeMapRef.current.get(id);
      if (!node) return false;

      responseAnchorActiveRef.current = true;
      responseAnchorIdRef.current = id;
      return scrollNodeIntoLog(node, behavior, "start");
    },
    [scrollNodeIntoLog]
  );

  const scrollToBusyAnchor = useCallback(
    (behavior = "smooth") => {
      const node = busyBubbleRef.current;
      if (!node) return false;

      responseAnchorActiveRef.current = true;
      responseAnchorIdRef.current = "__BUSY__";
      return scrollNodeIntoLog(node, behavior, "end");
    },
    [scrollNodeIntoLog]
  );

  const clearResponseAnchor = useCallback(() => {
    responseAnchorActiveRef.current = false;
    responseAnchorIdRef.current = "";
    awaitingBusyUserAnchorRef.current = false;
    busyBaselineUserIdRef.current = "";
  }, []);

  // Matches ChatInterface: ↓ is frontend-only and never touches history cursors.
  const jumpToLatest = () => {
    const el = logRef.current;
    if (!el) return;

    clearResponseAnchor();

    try {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    } catch {
      scrollToBottom("auto");
      return;
    }

    atBottomRef.current = true;
    setShowJumpToLatest(false);
  };

  const scrollRafRef = useRef(null);
  const onLogScroll = () => {
    if (scrollRafRef.current) return;

    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      syncScrollNavigationState();
    });
  };

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = logRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => {
      // Never chase the bottom during an anchored response cycle.
      if (responseAnchorActiveRef.current) return;

      if (atBottomRef.current || forceScrollOnceRef.current) {
        window.requestAnimationFrame(() => {
          scrollToBottom(forceScrollOnceRef.current ? "smooth" : "auto");
          forceScrollOnceRef.current = false;
          setShowJumpToLatest(false);
        });
      } else {
        syncScrollNavigationState();
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!feedSig) return;
    const intent = scrollIntentRef.current;
    if (!intent) return;

    scrollIntentRef.current = null;

    window.requestAnimationFrame(() => {
      if (intent.type === "anchor" && intent.messageId) {
        scrollToMessageAnchor(intent.messageId, intent.behavior || "smooth");
        return;
      }

      if (intent.type === "busy") {
        scrollToBusyAnchor(intent.behavior || "smooth");
        return;
      }

      scrollToBottom(intent.behavior || "auto");
      if (intent.clearJump) setShowJumpToLatest(false);
    });
  }, [feedSig, scrollToMessageAnchor, scrollToBusyAnchor]);

  // Any route can start a Lexa response cycle. The parent busy transition is the
  // authoritative moment when Lexa "kicks in", so ALWAYS bring Thinking/Analyzing
  // into view here. This applies equally to text chat and screen analysis.
  useEffect(() => {
    const wasBusy = previousParentBusyForAnchorRef.current;

    if (parentBusy && !wasBusy) {
      const current = Array.isArray(messagesRef.current) ? messagesRef.current : [];
      const latestUser = [...current]
        .reverse()
        .find((m) => m?.role === "user" && !m?.__hidden);

      // Retain the baseline only for diagnostics/legacy compatibility. We no
      // longer move the anchor back to a backend user echo after Lexa starts.
      busyBaselineUserIdRef.current = String(latestUser?.id || "");
      awaitingBusyUserAnchorRef.current = false;

      responseAnchorActiveRef.current = true;
      responseAnchorIdRef.current = "__BUSY__";

      scrollIntentRef.current = {
        type: "busy",
        behavior: "smooth",
        clearJump: true,
      };

      // Force one layout pass after the busy bubble mounts.
      setFeedSig(`lexa_busy_anchor:${Date.now()}`);
    }

    if (!parentBusy && wasBusy) {
      awaitingBusyUserAnchorRef.current = false;
    }

    previousParentBusyForAnchorRef.current = parentBusy;
  }, [parentBusy]);
  // ---------------- Helpers ----------------
  const toMillis = (d) => {
    try {
      if (!d) return 0;
      if (typeof d === "number") return d;
      if (typeof d === "string") {
        const ms = Date.parse(d);
        return Number.isFinite(ms) ? ms : 0;
      }
      if (typeof d?.toMillis === "function") return d.toMillis();
      if (typeof d?.toDate === "function") return d.toDate().getTime();
      return 0;
    } catch {
      return 0;
    }
  };

  const getClientCreatedAt = (raw) => {
    try {
      if (!raw) return 0;
      const v =
        raw.client_created_at ??
        raw.clientCreatedAt ??
        raw.client_created_at_ms ??
        raw?.meta?.client_created_at ??
        raw?.meta?.clientCreatedAt ??
        raw?.meta?.client_created_at_ms ??
        raw?.metadata?.client_created_at ??
        raw?.metadata?.clientCreatedAt ??
        raw?.metadata?.client_created_at_ms ??
        0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  // ✅ SERVER time ONLY (never use client_created_at here)
  const getServerCreatedAtMs = (raw) => {
    try {
      if (!raw) return 0;
      const v = raw.created_at_ms || raw.createdMs || toMillis(raw.created_at) || 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  const getUpdatedAtMs = (raw) => {
    try {
      if (!raw) return 0;
      const v =
        raw.updated_at_ms ??
        raw.updatedAtMs ??
        raw.updatedMs ??
        toMillis(raw.updated_at) ??
        raw?.meta?.updated_at_ms ??
        raw?.metadata?.updated_at_ms ??
        0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  const _normText = (s) => {
    try {
      return String(s || "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    } catch {
      return "";
    }
  };

  const _makeUserSig = (text, frameId) => {
    const t = _normText(text);
    const f = String(frameId || "").trim();
    // frameId is optional; keep deterministic but not required to match
    return `${t}::fid=${f}`;
  };

  const normalizeRows = (docs) => {
    const rows = (docs || []).map((item, idx) => {
      const raw = item?.raw || item || {};
      const role = String(raw.role || raw.sender || "system").toLowerCase();

      const clientCreatedAt = getClientCreatedAt(raw); // keep for matching only

      // ✅ IMPORTANT: do NOT fallback to Date.now() for ordering (that can invert order)
      const serverCreatedMs = getServerCreatedAtMs(raw) || 0;

      const updatedMs = getUpdatedAtMs(raw) || 0;

      const imageUrl = String(
        raw.image_url ||
          raw.imageUrl ||
          raw.file_url ||
          raw.fileUrl ||
          raw?.attachment?.image_url ||
          raw?.attachment?.imageUrl ||
          raw?.attachment?.file_url ||
          raw?.attachment?.fileUrl ||
          ""
      ).trim();

      const frameId = String(
        raw.frame_id ||
          raw.frameId ||
          raw?.attachment?.frame_id ||
          raw?.attachment?.frameId ||
          raw?.meta?.frame_id ||
          raw?.metadata?.frame_id ||
          ""
      ).trim();

      const frameIds = _extractFrameIdsFromMessage(raw);
      const imageUrls = _extractImageUrlsFromMessage(raw);

      const sessionDocPath = String(
        raw.session_doc_path ||
          raw.sessionDocPath ||
          raw?.attachment?.session_doc_path ||
          raw?.attachment?.sessionDocPath ||
          ""
      ).trim();

      const fileName =
        raw.file_name ||
        raw.fileName ||
        raw?.attachment?.file_name ||
        raw?.attachment?.fileName ||
        raw?.attachment?.name ||
        raw.name ||
        "";

      const mime =
        raw.mime ||
        raw.content_type ||
        raw.contentType ||
        raw?.attachment?.mime ||
        raw?.attachment?.content_type ||
        raw?.attachment?.contentType ||
        "";

      const language = String(
        raw.language ||
          raw.languageCode ||
          raw.language_code ||
          raw.responseLanguage ||
          raw.response_language ||
          raw?.meta?.language ||
          raw?.metadata?.language ||
          ""
      ).trim();

      const meta = raw.meta || raw.metadata || null;

      // ✅ DISPLAY TEXT: prevent JSON "lump" on assistant analyze replies
      let text = "";
      if (role === "assistant") {
        text = _extractAssistantTextFromAny(raw);
      } else {
        // user/system: keep existing behavior
        text = String(raw.text || raw.message || "");
      }

      // ✅ swallow known assistant one-liner status messages (do NOT render)
      const __hidden = role === "assistant" ? _isSwallowableAssistantStatusLine(text) : false;
      if (__hidden) text = "";

      const textLen = text.length;

      return {
        id: item.id || item.message_id || raw.id || `m_${Math.random().toString(16).slice(2)}`,
        role,
        name: role === "assistant" ? "Lexa" : role === "user" ? "You" : "Lexa",
        text,

        // this is not displayed; keep deterministic
        ts: serverCreatedMs ? new Date(serverCreatedMs).toISOString() : "",

        // ✅ createdMs is server timeline only (may be 0 if missing)
        createdMs: serverCreatedMs,
        serverCreatedMs,

        // ✅ sortMs is what UI uses for ordering (filled below)
        sortMs: serverCreatedMs || 0,

        clientCreatedAt,

        updatedMs,
        textLen,

        frame_id: frameId || frameIds[0] || null,
        frame_ids: frameIds.length ? frameIds : frameId ? [frameId] : [],
        session_doc_path: sessionDocPath || null,
        image_url: imageUrl || imageUrls[0] || null,
        image_urls: imageUrls.length ? imageUrls : imageUrl ? [imageUrl] : [],

        file_name: String(fileName || "").trim() || null,
        mime: String(mime || "").trim() || null,
        language: language || null,
        meta,
        raw,

        __serverIndex: idx, // stable tie-break
        __hidden,
      };
    });

    // ✅ Fill sortMs so missing timestamps never reorder messages incorrectly
    function applySortMs(list) {
      if (!Array.isArray(list) || list.length === 0) return;

      const known = [];
      for (let i = 0; i < list.length; i++) {
        const t = Number(list[i]?.createdMs || 0);
        if (t > 0) known.push({ i, t });
      }

      // No real server timestamps: preserve backend order with a synthetic monotonic clock
      if (known.length === 0) {
        let t = Date.now() - list.length;
        for (let i = 0; i < list.length; i++) {
          t += 1;
          list[i].sortMs = t;
          list[i].ts = new Date(t).toISOString();
        }
        return;
      }

      // Anchor known timestamps
      for (const k of known) {
        list[k.i].sortMs = k.t;
        list[k.i].ts = new Date(k.t).toISOString();
      }

      // Backfill before first anchor
      let t0 = Number(list[known[0].i].sortMs || known[0].t);
      for (let i = known[0].i - 1; i >= 0; i--) {
        t0 -= 1;
        list[i].sortMs = t0;
        list[i].ts = new Date(t0).toISOString();
      }

      // Fill gaps between anchors (monotonic)
      for (let a = 0; a < known.length - 1; a++) {
        const left = known[a];
        const right = known[a + 1];
        const li = left.i;
        const ri = right.i;

        let lt = Number(list[li].sortMs || left.t);
        let rt = Number(list[ri].sortMs || right.t);

        // Force monotonic window (do NOT alter createdMs)
        if (rt <= lt) rt = lt + (ri - li);

        const span = ri - li;
        for (let i = li + 1; i < ri; i++) {
          const frac = (i - li) / span;
          const t = Math.floor(lt + frac * (rt - lt));
          list[i].sortMs = t;
          list[i].ts = new Date(t).toISOString();
        }

        // Keep right anchor exact (or adjusted if needed)
        list[ri].sortMs = Number(list[ri].createdMs || rt);
        if (list[ri].sortMs <= list[ri - 1].sortMs) list[ri].sortMs = list[ri - 1].sortMs + 1;
        list[ri].ts = new Date(list[ri].sortMs).toISOString();
      }

      // Forward fill after last anchor
      let tN = Number(list[known[known.length - 1].i].sortMs || known[known.length - 1].t);
      for (let i = known[known.length - 1].i + 1; i < list.length; i++) {
        tN += 1;
        list[i].sortMs = tN;
        list[i].ts = new Date(tN).toISOString();
      }
    }

    applySortMs(rows);

    // ✅ UI sorts by sortMs (never by Date.now fallback)
    rows.sort((a, b) => {
      const da = Number(a.sortMs || a.createdMs || 0);
      const db = Number(b.sortMs || b.createdMs || 0);
      if (da !== db) return da - db;
      return Number(a.__serverIndex || 0) - Number(b.__serverIndex || 0);
    });

    return rows;
  };

  const makeLocalId = () => `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;

  const isSameUserMessage = (serverMsg, localMsg) => {
    try {
      if (!serverMsg || !localMsg) return false;
      if (serverMsg.role !== "user" || localMsg.role !== "user") return false;

      const sCC =
        Number(serverMsg.clientCreatedAt || getClientCreatedAt(serverMsg?.raw || serverMsg) || 0) || 0;
      const lCC =
        Number(localMsg.clientCreatedAt || getClientCreatedAt(localMsg?.raw || localMsg) || 0) || 0;

      if (sCC && lCC && sCC === lCC) return true;

      const sameText = String(serverMsg.text || "").trim() === String(localMsg.text || "").trim();
      if (!sameText) return false;

      const sT = Number(serverMsg.sortMs || serverMsg.createdMs || 0);
      const lT = Number(localMsg.sortMs || localMsg.createdMs || 0);
      const dt = Math.abs(sT - lT);
      return dt <= 15000; // 15s window
    } catch {
      return false;
    }
  };

  // ---------------- Older Vision history pagination ----------------
  // Matches ChatInterface: ↑ is the only navigation arrow that talks to the backend.
  const loadPreviousVisionHistoryRef = useRef(null);

  const loadPreviousVisionHistory = async () => {
    const sid = String(sessionId || "").trim();
    const cursor = String(historyNextCursorRef.current || historyNextCursor || "").trim();

    if (
      !sid ||
      !effectiveUid ||
      !historyHasMoreRef.current ||
      !cursor ||
      historyLoadingPreviousRef.current ||
      parentBusyRef.current ||
      isSending
    ) {
      return;
    }

    const scrollEl = logRef.current;
    const scrollSnapshot = scrollEl
      ? {
          sid,
          scrollHeight: scrollEl.scrollHeight || 0,
          scrollTop: scrollEl.scrollTop || 0,
        }
      : null;

    historyLoadingPreviousRef.current = true;
    setHistoryLoadingPrevious(true);

    try {
      const res = await getVisionMessages(
        sid,
        {},
        {
          pageSize: VISION_HISTORY_PAGE_SIZE,
          cursor,
          includeFrameUrls: false,
          includeFrameProxy: false,
        }
      );

      if (String(sessionId || "").trim() !== sid) return;

      const list = Array.isArray(res?.messages) ? res.messages : [];
      const olderRows = normalizeRows(
        list.map((m) => ({ id: m.id || m.message_id, raw: m }))
      );

      const existingIds = new Set(
        (messagesRef.current || [])
          .map((message) => String(message?.id || "").trim())
          .filter(Boolean)
      );

      const uniqueOlder = olderRows.filter((message) => {
        const id = String(message?.id || "").trim();
        return !id || !existingIds.has(id);
      });

      if (uniqueOlder.length > 0) {
        historyPrependScrollRestoreRef.current = scrollSnapshot;

        setMessages((previous) => {
          const currentIds = new Set(
            (previous || [])
              .map((message) => String(message?.id || "").trim())
              .filter(Boolean)
          );

          const prepend = uniqueOlder.filter((message) => {
            const id = String(message?.id || "").trim();
            return !id || !currentIds.has(id);
          });

          if (!prepend.length) return previous;

          return [...prepend, ...(previous || [])].sort((a, b) => {
            const da = Number(a?.sortMs || a?.createdMs || 0);
            const db = Number(b?.sortMs || b?.createdMs || 0);
            if (da !== db) return da - db;
            return Number(a?.__serverIndex || 0) - Number(b?.__serverIndex || 0);
          });
        });

        try {
          const need = [];
          for (const mm of uniqueOlder) {
            const ids = _uniqCleanStrArray([
              String(mm?.frame_id || "").trim(),
              ...(Array.isArray(mm?.frame_ids) ? mm.frame_ids : []),
            ]);
            for (const fid of ids) {
              if (!String(frameUrlMapRef.current?.[fid] || "").trim()) {
                need.push(fid);
              }
            }
          }
          Array.from(new Set(need))
            .slice(0, 20)
            .forEach((fid) => ensureFrameUrl(fid));
        } catch {}
      } else {
        historyPrependScrollRestoreRef.current = null;
      }

      setHistoryCursorState(
        res?.has_more === true,
        res?.next_cursor || ""
      );
    } catch (error) {
      try {
        console.warn(
          "[VisionChatInterface] previous history load failed:",
          error?.message || error
        );
      } catch {}
    } finally {
      historyLoadingPreviousRef.current = false;
      setHistoryLoadingPrevious(false);
    }
  };

  loadPreviousVisionHistoryRef.current = loadPreviousVisionHistory;

  // Parent thumbnail requests resolve against already-loaded messages first.
  // Every click is authoritative: a newer frame click immediately replaces any
  // older pending jump/highlight. If the latest qualifying single-frame
  // response is not loaded yet, reuse the existing ↑ pagination only until one
  // is found. Multi-frame analyses remain excluded.
  useEffect(() => {
    const rawRequest =
      frameAnchorRequest && typeof frameAnchorRequest === "object"
        ? frameAnchorRequest
        : null;

    if (!rawRequest) return;

    const frameId = String(rawRequest.frameId || rawRequest.frame_id || "").trim();
    const requestId = String(
      rawRequest.requestId ??
        rawRequest.request_id ??
        `${frameId}:${Date.now()}`
    ).trim();

    if (!frameId || frameId === "__LIVE__" || !requestId) return;

    const requestKey = `${frameId}:${requestId}`;
    if (handledFrameAnchorRequestIdRef.current === requestKey) return;

    handledFrameAnchorRequestIdRef.current = requestKey;

    const generation = Number(frameAnchorGenerationRef.current || 0) + 1;
    frameAnchorGenerationRef.current = generation;

    pendingFrameAnchorRef.current = {
      frameId,
      requestId,
      generation,
    };

    // A new thumbnail click immediately releases the previous 1.8s highlight,
    // live-response anchor, queued scroll intent, and any in-progress smooth
    // movement. The newest click always wins.
    clearHistoryHighlight();
    clearResponseAnchor();
    scrollIntentRef.current = null;

    const log = logRef.current;
    if (log) {
      try {
        // Assigning the current position with auto behavior interrupts a native
        // smooth-scroll animation without changing where the user is.
        if (typeof log.scrollTo === "function") {
          log.scrollTo({
            top: log.scrollTop || 0,
            behavior: "auto",
          });
        }
      } catch {}
    }

    // Ref changes alone do not trigger React effects. This tick guarantees that
    // EVERY thumbnail click runs the resolver immediately.
    setFrameAnchorRequestTick((value) => value + 1);
  }, [
    frameAnchorRequest,
    clearHistoryHighlight,
    clearResponseAnchor,
  ]);

  useEffect(() => {
    const pending = pendingFrameAnchorRef.current;
    if (!pending?.frameId) return;

    const generation = Number(pending.generation || 0);
    if (!generation || generation !== frameAnchorGenerationRef.current) return;

    // Do not decide until the initial newest-page fetch has finished.
    if (!historyInitializedRef.current) return;

    // Search what is already rendered FIRST. Because history starts with the
    // newest 40 messages, the latest qualifying single-frame analysis/reanalysis
    // is already authoritative whenever it is present. Multi-frame analysis is
    // never considered a target.
    let target = _findLatestSingleFrameAssistantMessage(
      messages,
      pending.frameId,
      {
        allowAssistantOnlyFallback: !historyHasMoreRef.current,
      }
    );

    if (target?.id) {
      const targetId = String(target.id || "").trim();

      window.requestAnimationFrame(() => {
        // Another thumbnail may have been clicked before this frame rendered.
        // If so, this scheduled jump is stale and must do nothing.
        if (generation !== frameAnchorGenerationRef.current) return;

        const node = messageNodeMapRef.current.get(targetId);
        if (!node) return;

        // Historical navigation is intentionally immediate. The brief highlight
        // supplies orientation without making the user wait for a long smooth
        // animation before the next click can take effect.
        scrollNodeIntoLog(node, "auto", "start");
        highlightHistoricalMessage(targetId);

        const current = pendingFrameAnchorRef.current;
        if (Number(current?.generation || 0) === generation) {
          pendingFrameAnchorRef.current = null;
        }
      });
      return;
    }

    // No qualifying single-frame response is loaded yet. Load one older page,
    // then this effect reruns. A newer click can replace pendingFrameAnchorRef at
    // any time; the old lookup never owns the interface.
    if (historyHasMoreRef.current) {
      if (
        !historyLoadingPreviousRef.current &&
        !parentBusy &&
        !isSending
      ) {
        const loader = loadPreviousVisionHistoryRef.current;
        if (typeof loader === "function") {
          loader().catch(() => {});
        }
      }
      return;
    }

    if (historyLoadingPreviousRef.current) return;

    // History is exhausted. Run the compatibility fallback one last time for
    // older records that stored frame_id only on the Lexa response.
    target = _findLatestSingleFrameAssistantMessage(
      messages,
      pending.frameId,
      {
        allowAssistantOnlyFallback: true,
      }
    );

    if (target?.id) {
      const targetId = String(target.id || "").trim();

      window.requestAnimationFrame(() => {
        if (generation !== frameAnchorGenerationRef.current) return;

        const node = messageNodeMapRef.current.get(targetId);
        if (!node) return;

        scrollNodeIntoLog(node, "auto", "start");
        highlightHistoricalMessage(targetId);

        const current = pendingFrameAnchorRef.current;
        if (Number(current?.generation || 0) === generation) {
          pendingFrameAnchorRef.current = null;
        }
      });
      return;
    }

    // No qualifying single-frame Lexa response exists.
    // Keep the screenshot selected and silently finish navigation.
    if (generation === frameAnchorGenerationRef.current) {
      pendingFrameAnchorRef.current = null;
    }
  }, [
    frameAnchorRequestTick,
    messages,
    historyHasMore,
    historyLoadingPrevious,
    parentBusy,
    isSending,
    scrollNodeIntoLog,
    highlightHistoricalMessage,
  ]);

  // Preserve the exact visible reading position when older history is prepended.
  useLayoutEffect(() => {
    const restore = historyPrependScrollRestoreRef.current;
    if (!restore) return;
    if (String(restore.sid || "") !== String(sessionId || "")) {
      historyPrependScrollRestoreRef.current = null;
      return;
    }

    const el = logRef.current;
    if (!el) {
      historyPrependScrollRestoreRef.current = null;
      return;
    }

    const heightDelta = Math.max(
      0,
      (el.scrollHeight || 0) - Number(restore.scrollHeight || 0)
    );

    try {
      el.scrollTop = Number(restore.scrollTop || 0) + heightDelta;
    } catch {}

    historyPrependScrollRestoreRef.current = null;
    syncScrollNavigationState();
  }, [messages.length, sessionId]);

  // ---------------- Backend message feed (NO Firestore) ----------------
  const lastSignatureRef = useRef("");

  useEffect(() => {
    let alive = true;
    let pollTimer = null;
    let inFlight = false;

    const sessionKey = `${String(effectiveUid || "").trim()}::${String(sessionId || "").trim()}`;
    const shouldHardReset =
      !!effectiveUid && !!sessionId && sessionKey && prevSessionKeyRef.current !== sessionKey;

    if (shouldHardReset) {
      prevSessionKeyRef.current = sessionKey;

      lastSignatureRef.current = "";
      setMessages([]);
      setShowJumpToLatest(false);
      setHistoryCursorState(false, "");
      historyLoadingPreviousRef.current = false;
      setHistoryLoadingPrevious(false);
      historyInitializedRef.current = false;
      historyPrependScrollRestoreRef.current = null;
      setFeedError("");
      greetedRef.current = false;

      lastServerCreatedMsRef.current = 0;
      nextLocalSortMsRef.current = 0;

      // ✅ clear pending-local registry on session switch
      pendingLocalRef.current = new Map();

      setFrameUrlMap({});
      frameRetryRef.current = {};
      inflightFrameRef.current = new Set();
      badDirectFrameRef.current = new Set();

      // ✅ reset busy delay trackers on session switch
      lastAssistantSeenIdRef.current = "";
      delayArmedForBusyCycleRef.current = false;
      if (busyDelayTimerRef.current) {
        clearTimeout(busyDelayTimerRef.current);
        busyDelayTimerRef.current = null;
      }
      setBusyDelayUntilMs(0);

      // ✅ reset manual upload notify dedupe
      manualUploadSeqRef.current = 0;
      lastManualUploadKeyRef.current = "";

      clearResponseAnchor();
      clearHistoryHighlight();
      pendingFrameAnchorRef.current = null;
      handledFrameAnchorRequestIdRef.current = "";
      frameAnchorGenerationRef.current = 0;
      setFrameAnchorRequestTick(0);
      messageNodeMapRef.current = new Map();
      forceScrollOnceRef.current = true;
    }

    function computeMatchedLocalIds(rows, locals) {
      const matchedLocalIds = new Set();
      try {
        const pending = pendingLocalRef.current || new Map();
        if (!pending.size) return matchedLocalIds;

        // Build server user candidates once
        const serverUsers = rows.filter((r) => r?.role === "user");
        const usedServerIds = new Set();

        // Sort pending entries by clientCreatedAt ascending
        const entries = Array.from(pending.entries()).sort((a, b) => Number(a[0]) - Number(b[0]));

        for (const [ccaKey, p] of entries) {
          if (!p || !p.localId) continue;

          const cca = Number(ccaKey || 0);
          const baseline = Number(p.baselineServer || 0);

          // 1) Strong match: server echoes clientCreatedAt
          let match =
            cca > 0
              ? serverUsers.find(
                  (r) => !usedServerIds.has(r.id) && Number(r?.clientCreatedAt || 0) === cca
                )
              : null;

          // 2) Signature match after baseline (covers long-running analyze)
          if (!match) {
            match = serverUsers.find((r) => {
              if (usedServerIds.has(r.id)) return false;
              const t = Number(r?.sortMs || r?.createdMs || 0);
              if (baseline && t <= baseline) return false;

              const rTextNorm = _normText(r?.text || "");
              const rFrameId = String(r?.frame_id || "").trim();
              const rSig = _makeUserSig(rTextNorm, rFrameId);

              // If both sides have frameId, require it to match; otherwise fall back to text-only signature
              if (String(p.frameId || "").trim() && rFrameId) {
                return rSig === p.textSig;
              }
              // text-only match (ignore frameId) if needed
              const pTextOnly = String(p.textNorm || "");
              return rTextNorm === pTextOnly;
            });
          }

          if (match) {
            matchedLocalIds.add(p.localId);
            usedServerIds.add(match.id);
            // remove matched pending so it doesn't grow
            try {
              pending.delete(ccaKey);
            } catch {}
          }
        }
      } catch {
        // no-op
      }
      return matchedLocalIds;
    }

    function clearPollTimer() {
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    }

    function computeNextDelayMs() {
      try {
        // If tab is hidden, go slower
        if (typeof document !== "undefined" && document.hidden) return 15000;

        // Busy/analyzing OR waiting for server-echo => fast
        const pendingSize = Number(pendingLocalRef.current?.size || 0);
        if (parentBusyRef.current) return 1000;
        if (pendingSize > 0) return 1000;

        const now = Date.now();
        const lastAct = Number(lastActivityMsRef.current || 0);

        // If user is typing (non-empty), keep medium polling
        if (Number(inputLenRef.current || 0) > 0) return 3000;

        // Recent activity window => medium
        if (now - lastAct < 15000) return 3000;

        // Otherwise idle => slow
        return 20000;
      } catch {
        return 5000;
      }
    }

    async function fetchOnce() {
      if (!sessionId) return;
      if (!effectiveUid) return;
      if (historyLoadingPreviousRef.current) return;

      if (inFlight) return;
      inFlight = true;

      try {
        const res = await getVisionMessages(
          sessionId,
          {},
          {
            pageSize: VISION_HISTORY_PAGE_SIZE,
            includeFrameUrls: false,
            includeFrameProxy: false,
          }
        );
        if (!alive) return;

        if (!historyInitializedRef.current) {
          historyInitializedRef.current = true;
          setHistoryCursorState(
            res?.has_more === true,
            res?.next_cursor || ""
          );
        }

        const list = Array.isArray(res?.messages) ? res.messages : [];
        const rows = normalizeRows(list.map((m) => ({ id: m.id || m.message_id, raw: m })));

        // ✅ server-aligned max uses sortMs (robust even if createdMs missing)
        try {
          const maxT = rows.reduce(
            (acc, r) => Math.max(acc, Number(r?.sortMs || r?.createdMs || 0)),
            0
          );
          lastServerCreatedMsRef.current = Math.max(lastServerCreatedMsRef.current || 0, maxT || 0);
        } catch {}

        const last = rows[rows.length - 1];
        const lastT = Number(last?.sortMs || last?.createdMs || 0) || 0;

        const sig = `${rows.length}:${last?.id || ""}:${lastT}:${last?.updatedMs || 0}:${last?.textLen || 0}`;
        const prevSig = lastSignatureRef.current;

        setFeedError("");

        const locals = (messagesRef.current || []).filter((m) => m?.raw?.__local);

        // ✅ Robust dedupe of optimistic local user messages
        const matchedLocalIds = computeMatchedLocalIds(rows, locals);

        // Keep locals only if they are not matched by registry AND not matched by older heuristic
        const stillPending = locals.filter((l) => {
          if (!l) return false;
          if (matchedLocalIds.has(l.id)) return false;
          // keep old heuristic as extra safety
          return !rows.some((r) => isSameUserMessage(r, l));
        });

        // Preserve any older pages the user explicitly loaded. Polling refreshes
        // only the newest 40; it must never collapse the visible history back to
        // one page. Fresh rows replace same-ID rows, while older server rows stay.
        const serverById = new Map();

        for (const existing of messagesRef.current || []) {
          if (!existing || existing?.raw?.__local) continue;
          const id = String(existing?.id || "").trim();
          if (!id) continue;
          serverById.set(id, existing);
        }

        for (const row of rows) {
          const id = String(row?.id || "").trim();
          if (!id) continue;
          serverById.set(id, row);
        }

        // ✅ Merge and sort by sortMs (never by Date.now fallback)
        const merged = [...serverById.values(), ...stillPending].sort((a, b) => {
          const da = Number(a?.sortMs || a?.createdMs || 0);
          const db = Number(b?.sortMs || b?.createdMs || 0);
          if (da !== db) return da - db;
          const ia = Number(a?.__serverIndex || 0);
          const ib = Number(b?.__serverIndex || 0);
          return ia - ib;
        });

        if (parentBusyRef.current && awaitingBusyUserAnchorRef.current) {
          const latestUser = [...merged]
            .reverse()
            .find((m) => m?.role === "user" && !m?.__hidden);

          const latestUserId = String(latestUser?.id || "");
          const baselineUserId = String(busyBaselineUserIdRef.current || "");

          if (latestUserId && latestUserId !== baselineUserId) {
            responseAnchorActiveRef.current = true;
            responseAnchorIdRef.current = latestUserId;
            awaitingBusyUserAnchorRef.current = false;

            scrollIntentRef.current = {
              type: "anchor",
              messageId: latestUserId,
              behavior: "smooth",
              clearJump: true,
            };
          }
        }

        const shouldAuto =
          !responseAnchorActiveRef.current &&
          (atBottomRef.current || forceScrollOnceRef.current);

        const pendingSig = stillPending.map((m) => `${m.id}:${m.text?.length || 0}`).join("|");
        const combinedSig = `${sig}|pending:${stillPending.length}:${pendingSig}`;

        if (combinedSig !== prevSig) {
          lastSignatureRef.current = combinedSig;

          // ✅ treat message change as "activity" for adaptive polling
          lastActivityMsRef.current = Date.now();

          setMessages(merged);
          setFeedSig(combinedSig);

          try {
            const need = [];
            for (const mm of merged) {
              const ids = _uniqCleanStrArray([
                String(mm?.frame_id || "").trim(),
                ...(Array.isArray(mm?.frame_ids) ? mm.frame_ids : []),
              ]);

              for (const fid of ids) {
                const hasCached = String(frameUrlMapRef.current?.[fid] || "").trim();
                if (!hasCached) need.push(fid);
              }
            }
            const uniq = Array.from(new Set(need)).slice(0, 20);
            uniq.forEach((fid) => ensureFrameUrl(fid));
          } catch {}

          if (shouldAuto) {
            scrollIntentRef.current = {
              type: "bottom",
              behavior: forceScrollOnceRef.current ? "smooth" : "auto",
              clearJump: true,
            };
            forceScrollOnceRef.current = false;
          } else if (responseAnchorActiveRef.current) {
            setShowJumpToLatest(false);
          } else {
            // Content can grow without a scroll event. Recompute ↓ after DOM update.
            window.requestAnimationFrame(() => {
              syncScrollNavigationState();
            });
          }
        }
      } catch (e) {
        if (!alive) return;
        const msg = e?.message || "Failed to read messages from backend";
        setFeedError(msg);
        try {
          console.warn("[VisionChatInterface] backend feed error:", msg);
        } catch {}
      } finally {
        inFlight = false;
      }
    }

    function scheduleNext(msOverride = null) {
      if (!alive) return;
      clearPollTimer();
      const ms = Number.isFinite(Number(msOverride)) ? Number(msOverride) : computeNextDelayMs();
      pollTimer = setTimeout(async () => {
        if (!alive) return;
        await fetchOnce();
        scheduleNext();
      }, Math.max(250, ms));
    }

    function onVisibilityChange() {
      try {
        if (!alive) return;
        if (!document.hidden) {
          lastActivityMsRef.current = Date.now();
          // immediate refresh when tab becomes visible
          fetchOnce();
          // then resume adaptive polling cadence
          scheduleNext(1000);
        }
      } catch {}
    }

    if (!sessionId || !effectiveUid) {
      return () => {
        alive = false;
        clearPollTimer();
      };
    }

    // Initial load + start adaptive polling loop
    fetchOnce();
    scheduleNext(1000);

    // Wake-up trigger
    try {
      document.addEventListener("visibilitychange", onVisibilityChange);
    } catch {}

    return () => {
      alive = false;
      clearPollTimer();
      try {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      } catch {}
    };
  }, [
    sessionId,
    effectiveUid,
    ensureFrameUrl,
    clearResponseAnchor,
    clearHistoryHighlight,
  ]);

  // Arm busy-delay when a NEW backend assistant message arrives (once per busy cycle)
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;

    // Find latest assistant message (backend-origin)
    // - ignore local error bubbles
    // - ignore any "__local" messages (if any ever exist for assistant)
    // - ignore swallowed assistant status one-liners
    let latest = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!m) continue;
      if (m.role !== "assistant") continue;
      if (m.__hidden) continue;
      if (!String(m.text || "").trim()) continue;
      const id = String(m.id || "");
      if (id.startsWith("local_err_")) continue;
      if (m?.raw?.__local) continue;
      latest = m;
      break;
    }

    const latestId = String(latest?.id || "");
    if (!latestId) return;

    if (latestId === lastAssistantSeenIdRef.current) return;
    lastAssistantSeenIdRef.current = latestId;

    // If this assistant message belongs to an active response cycle, Lexa's
    // actual answer becomes the permanent viewport anchor. This works even if
    // parent busy flips false in the same polling/render cycle.
    if (responseAnchorActiveRef.current) {
      responseAnchorIdRef.current = latestId;
      awaitingBusyUserAnchorRef.current = false;

      scrollIntentRef.current = {
        type: "anchor",
        messageId: latestId,
        behavior: "smooth",
        clearJump: true,
      };

      // The message node exists in this render; the feed signature schedules
      // the layout-phase scroll after refs are committed.
      setFeedSig(`lexa_answer_anchor:${latestId}:${Date.now()}`);
    }

    // Busy-bubble delay logic below only matters while parent is still busy.
    if (!parentBusy) return;

    // Only arm once per busy cycle (prevents chunking from continuously hiding)
    if (delayArmedForBusyCycleRef.current) return;
    delayArmedForBusyCycleRef.current = true;

    const until = Date.now() + BUSY_AFTER_ASSISTANT_DELAY_MS;
    setBusyDelayUntilMs(until);

    if (busyDelayTimerRef.current) clearTimeout(busyDelayTimerRef.current);
    busyDelayTimerRef.current = setTimeout(() => {
      setBusyDelayUntilMs(0);
    }, BUSY_AFTER_ASSISTANT_DELAY_MS);
  }, [messages, parentBusy]);

  // ✅ Auto greet only once
  const greetedRef = useRef(false);

  // Backend feed error (stored, but NOT displayed as a banner)
  const [feedError, setFeedError] = useState("");

  // ---------------- Speak / Copy / Edit ----------------
  const getTtsAuthHeaders = useCallback(async () => {
    const token = await getIdTokenSafe();
    if (!token) return null;

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [authUser]);

  // --------------------------
  // ✅ Lexa Read Aloud — streaming backend first, browser fallback second
  // --------------------------
  const ttsUtterRef = useRef(null);
  const ttsAbortRef = useRef(null);
  const ttsRequestIdRef = useRef(0);

  // Streaming PCM / Web Audio ownership.
  const ttsAudioContextRef = useRef(null);
  const ttsPcmSourcesRef = useRef(new Set());
  const ttsNextPlaybackTimeRef = useRef(0);
  const ttsStreamCompleteRef = useRef(false);

  const clearTtsPcmPlayback = useCallback(() => {
    const sources =
      ttsPcmSourcesRef.current ||
      new Set();

    for (const source of sources) {
      try {
        source.onended = null;
        source.stop?.(0);
      } catch {}

      try {
        source.disconnect?.();
      } catch {}
    }

    sources.clear();

    const context =
      ttsAudioContextRef.current;

    ttsAudioContextRef.current = null;
    ttsNextPlaybackTimeRef.current = 0;
    ttsStreamCompleteRef.current = false;

    if (context) {
      try {
        const closing =
          context.close?.();

        if (
          closing &&
          typeof closing.catch ===
            "function"
        ) {
          closing.catch(() => {});
        }
      } catch {}
    }
  }, []);

  const stopActiveTts = useCallback(() => {
    // Invalidate every old async callback before aborting it.
    ttsRequestIdRef.current += 1;

    try {
      ttsAbortRef.current?.abort?.();
    } catch {}

    ttsAbortRef.current = null;

    clearTtsPcmPlayback();

    try {
      if (
        typeof window !== "undefined"
      ) {
        window.speechSynthesis?.cancel?.();
      }
    } catch {}

    ttsUtterRef.current = null;
  }, [clearTtsPcmPlayback]);

  const createTtsAudioContext =
    useCallback(() => {
      if (
        typeof window === "undefined"
      ) {
        return null;
      }

      const AudioContextCtor =
        window.AudioContext ||
        window.webkitAudioContext;

      if (!AudioContextCtor) {
        return null;
      }

      clearTtsPcmPlayback();

      try {
        // Use the browser's native output rate. AudioBuffer is explicitly
        // created at 24 kHz, and Web Audio performs output resampling safely.
        const context =
          new AudioContextCtor();

        ttsAudioContextRef.current =
          context;
        ttsNextPlaybackTimeRef.current = 0;
        ttsStreamCompleteRef.current = false;

        // Call resume from the speaker click path so browser autoplay policy
        // sees a user gesture before the network request begins.
        try {
          const resuming =
            context.resume?.();

          if (
            resuming &&
            typeof resuming.catch ===
              "function"
          ) {
            resuming.catch(() => {});
          }
        } catch {}

        return context;
      } catch (error) {
        console.warn(
          "[TTS] Web Audio context unavailable:",
          error?.message || error
        );
        return null;
      }
    }, [clearTtsPcmPlayback]);

  const scheduleTtsPcmSamples =
    useCallback(
      (
        samples,
        context,
        requestId
      ) => {
        if (
          requestId !==
            ttsRequestIdRef.current ||
          !context ||
          context.state === "closed" ||
          !samples?.length
        ) {
          return false;
        }

        const audioBuffer =
          context.createBuffer(
            TTS_PCM_CHANNELS,
            samples.length,
            TTS_PCM_SAMPLE_RATE_HZ
          );

        audioBuffer.copyToChannel(
          samples,
          0
        );

        const source =
          context.createBufferSource();

        source.buffer = audioBuffer;
        source.connect(
          context.destination
        );

        // Keep a healthy rolling scheduling lead so Web Audio never has to
        // chase the network edge. This is the main frontend protection against
        // tiny underrun ticks between PCM blocks.
        const minimumLeadSeconds =
          TTS_PCM_MIN_SCHEDULE_LEAD_MS /
          1000;

        const startAt = Math.max(
          context.currentTime +
            minimumLeadSeconds,
          Number(
            ttsNextPlaybackTimeRef.current ||
              0
          )
        );

        const endAt =
          startAt +
          audioBuffer.duration;

        ttsNextPlaybackTimeRef.current =
          endAt;

        ttsPcmSourcesRef.current.add(
          source
        );

        source.onended = () => {
          try {
            source.disconnect?.();
          } catch {}

          ttsPcmSourcesRef.current.delete(
            source
          );

          if (
            requestId ===
              ttsRequestIdRef.current &&
            ttsStreamCompleteRef.current &&
            ttsPcmSourcesRef.current.size ===
              0 &&
            ttsAudioContextRef.current ===
              context
          ) {
            ttsAudioContextRef.current =
              null;
            ttsNextPlaybackTimeRef.current =
              0;

            try {
              const closing =
                context.close?.();

              if (
                closing &&
                typeof closing.catch ===
                  "function"
              ) {
                closing.catch(() => {});
              }
            } catch {}
          }
        };

        source.start(startAt);
        return true;
      },
      []
    );

  const speakWithBrowserFallback =
    useCallback(
      (
        rawText,
        languageCode
      ) => {
        try {
          if (
            typeof window === "undefined"
          ) {
            return false;
          }

          const locale =
            normalizeTtsLocale(
              languageCode
            ) ||
            TTS_DEFAULT_LANGUAGE;

          const speechText =
            prepareBrowserSpeechText(
              rawText,
              locale
            );

          if (!speechText) {
            return false;
          }

          const synth =
            window.speechSynthesis;

          const Utter =
            window.SpeechSynthesisUtterance;

          if (!synth || !Utter) {
            return false;
          }

          try {
            synth.cancel();
          } catch {}

          const utterance =
            new Utter(speechText);

          utterance.lang = locale;
          utterance.rate = 1;
          utterance.pitch = 1;
          utterance.volume = 1;

          try {
            const voices =
              synth.getVoices?.() || [];

            const normalizedLocale =
              locale
                .toLowerCase()
                .replace(/_/g, "-");

            const baseLanguage =
              normalizedLocale.split(
                "-"
              )[0];

            const voiceLocale = (
              voice
            ) =>
              String(
                voice?.lang || ""
              )
                .toLowerCase()
                .replace(/_/g, "-");

            const knownFemale = (
              voice
            ) =>
              FEMALE_BROWSER_VOICE_RE.test(
                String(
                  voice?.name || ""
                )
              );

            const preferred =
              voices.find(
                (voice) =>
                  voiceLocale(voice) ===
                    normalizedLocale &&
                  knownFemale(voice)
              ) ||
              voices.find(
                (voice) =>
                  voiceLocale(
                    voice
                  ).startsWith(
                    `${baseLanguage}-`
                  ) &&
                  knownFemale(voice)
              ) ||
              voices.find(
                (voice) =>
                  voiceLocale(voice) ===
                  normalizedLocale
              ) ||
              voices.find(
                (voice) =>
                  voiceLocale(
                    voice
                  ).startsWith(
                    `${baseLanguage}-`
                  ) ||
                  voiceLocale(voice) ===
                    baseLanguage
              ) ||
              voices.find(
                knownFemale
              ) ||
              null;

            if (preferred) {
              utterance.voice =
                preferred;
            }
          } catch {}

          utterance.onend = () => {
            if (
              ttsUtterRef.current ===
              utterance
            ) {
              ttsUtterRef.current =
                null;
            }
          };

          utterance.onerror = (
            event
          ) => {
            console.warn(
              "[TTS][browser] fallback failed:",
              event?.error ||
                event
            );

            if (
              ttsUtterRef.current ===
              utterance
            ) {
              ttsUtterRef.current =
                null;
            }
          };

          ttsUtterRef.current =
            utterance;

          console.log(
            "[TTS][browser] fallback started",
            {
              language: locale,
              chars:
                speechText.length,
            }
          );

          synth.speak(utterance);
          return true;
        } catch (error) {
          console.warn(
            "[TTS][browser] fallback unavailable:",
            error?.message ||
              error
          );
          return false;
        }
      },
      []
    );

  const handleSpeak = async (
    rawText,
    explicitLanguage = ""
  ) => {
    const textToSpeak =
      String(rawText || "").trim();

    if (!textToSpeak) return;

    // Stop earlier backend audio, browser speech, and in-flight fetches.
    stopActiveTts();

    const requestId =
      ++ttsRequestIdRef.current;

    const dominantLanguageHint =
      inferTtsDominantLocale(
        textToSpeak,
        explicitLanguage,
        (typeof navigator !==
        "undefined"
          ? navigator.language
          : "")
      );

    const backendUrl =
      getTtsBackendUrl();

    const fallbackToBrowser =
      () => {
        if (
          requestId !==
          ttsRequestIdRef.current
        ) {
          return false;
        }

        return speakWithBrowserFallback(
          textToSpeak,
          dominantLanguageHint
        );
      };

    if (
      !backendUrl ||
      !(authUser || getAuth()?.currentUser)
    ) {
      console.warn(
        "[TTS] backend unavailable before request; using browser fallback",
        {
          backendConfigured:
            !!backendUrl,
          authenticated:
            !!(authUser || getAuth()?.currentUser),
          dominantLanguageHint,
        }
      );

      fallbackToBrowser();
      return;
    }

    // Create/resume Web Audio synchronously in the click path before awaiting
    // Firebase/network work. This avoids autoplay-policy surprises later.
    const audioContext =
      createTtsAudioContext();

    if (!audioContext) {
      console.warn(
        "[TTS] Web Audio unavailable; using browser fallback"
      );
      fallbackToBrowser();
      return;
    }

    let controller = null;
    let firstAudioTimeoutId = null;
    let stallTimeoutId = null;
    let reader = null;

    let receivedAudioBytes = 0;
    let scheduledBackendAudio = false;

    // Incoming fetch chunks are arbitrary network boundaries. Keep one rolling
    // byte queue and release stable PCM blocks to Web Audio.
    let pcmQueueBytes =
      new Uint8Array(0);
    let oddByteCarry =
      new Uint8Array(0);

    const clearFirstAudioTimeout =
      () => {
        if (
          firstAudioTimeoutId
        ) {
          clearTimeout(
            firstAudioTimeoutId
          );
          firstAudioTimeoutId =
            null;
        }
      };

    const clearStallTimeout =
      () => {
        if (stallTimeoutId) {
          clearTimeout(
            stallTimeoutId
          );
          stallTimeoutId =
            null;
        }
      };

    const abortCurrentRequest =
      (reason) => {
        if (
          requestId !==
          ttsRequestIdRef.current
        ) {
          return;
        }

        console.warn(
          "[TTS] aborting backend stream:",
          reason
        );

        try {
          controller?.abort?.();
        } catch {}
      };

    const armStreamStallTimeout =
      () => {
        clearStallTimeout();

        stallTimeoutId = setTimeout(
          () => {
            abortCurrentRequest(
              "stream stalled"
            );
          },
          TTS_STREAM_STALL_TIMEOUT_MS
        );
      };

    const consumePcmBytes = (
      incomingBytes
    ) => {
      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return false;
      }

      const incoming =
        incomingBytes instanceof
        Uint8Array
          ? incomingBytes
          : new Uint8Array(0);

      if (!incoming.length) {
        return false;
      }

      const combined =
        concatUint8Arrays(
          oddByteCarry,
          incoming
        );

      const evenLength =
        combined.length -
        (combined.length %
          TTS_PCM_SAMPLE_WIDTH_BYTES);

      if (evenLength <= 0) {
        oddByteCarry =
          combined.slice();
        return false;
      }

      const playableBytes =
        combined.subarray(
          0,
          evenLength
        );

      oddByteCarry =
        evenLength <
        combined.length
          ? combined
              .subarray(
                evenLength
              )
              .slice()
          : new Uint8Array(0);

      const samples =
        pcm16LeBytesToFloat32(
          playableBytes
        );

      if (!samples.length) {
        return false;
      }

      const scheduled =
        scheduleTtsPcmSamples(
          samples,
          audioContext,
          requestId
        );

      if (scheduled) {
        scheduledBackendAudio = true;
      }

      return scheduled;
    };

    const drainPcmQueue = (
      {
        force = false,
      } = {}
    ) => {
      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return false;
      }

      let scheduledAny = false;

      // Before first playback, hold the slightly larger startup reserve.
      if (
        !scheduledBackendAudio &&
        !force &&
        pcmQueueBytes.length <
          TTS_PCM_START_BUFFER_BYTES
      ) {
        return false;
      }

      while (pcmQueueBytes.length) {
        // During normal streaming, emit fixed-size 120 ms blocks. At final
        // stream completion, force=true flushes the remaining short tail.
        if (
          !force &&
          pcmQueueBytes.length <
            TTS_PCM_PLAYBACK_BLOCK_BYTES
        ) {
          break;
        }

        const requestedLength =
          force
            ? Math.min(
                pcmQueueBytes.length,
                TTS_PCM_PLAYBACK_BLOCK_BYTES
              )
            : TTS_PCM_PLAYBACK_BLOCK_BYTES;

        const evenLength =
          requestedLength -
          (requestedLength %
            TTS_PCM_SAMPLE_WIDTH_BYTES);

        if (evenLength <= 0) {
          break;
        }

        const block =
          pcmQueueBytes
            .subarray(
              0,
              evenLength
            )
            .slice();

        pcmQueueBytes =
          pcmQueueBytes
            .subarray(
              evenLength
            )
            .slice();

        if (
          consumePcmBytes(block)
        ) {
          scheduledAny = true;
        }

        if (
          !scheduledBackendAudio &&
          !force
        ) {
          break;
        }
      }

      return scheduledAny;
    };

    try {
      const headers =
        await getTtsAuthHeaders();

      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return;
      }

      if (!headers) {
        throw new Error(
          "TTS authentication is not ready."
        );
      }

      controller =
        typeof AbortController !==
        "undefined"
          ? new AbortController()
          : null;

      ttsAbortRef.current =
        controller;

      // Because the backend does not send HTTP 200 until it has the first Google
      // PCM chunk, this timer measures "can backend/provider actually start?"
      if (controller) {
        firstAudioTimeoutId =
          setTimeout(() => {
            abortCurrentRequest(
              "first audio timeout"
            );
          }, TTS_FIRST_AUDIO_TIMEOUT_MS);
      }

      console.log(
        "[TTS] requesting streaming backend",
        {
          route: TTS_ROUTE,
          transport:
            "streaming-pcm",
          audio:
            "pcm_s16le",
          sampleRate:
            TTS_PCM_SAMPLE_RATE_HZ,
          channels:
            TTS_PCM_CHANNELS,
          dominantLanguageHint,
          firstAudioTimeoutMs:
            TTS_FIRST_AUDIO_TIMEOUT_MS,
          chars:
            textToSpeak.length,
        }
      );

      const response =
        await fetch(
          backendUrl,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              text: textToSpeak,

              // Frontend decides dominant language only. Backend owns final
              // mixed-language/code-switch segmentation.
              language_hint:
                dominantLanguageHint,

              clean_markdown: true,
              include_sources: false,
            }),
            ...(controller
              ? {
                  signal:
                    controller.signal,
                }
              : null),
          }
        );

      // Backend prefetches first provider PCM before it commits HTTP 200.
      // If fetch resolved with 200, Google has already produced audio.
      clearFirstAudioTimeout();

      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return;
      }

      if (!response.ok) {
        const detail =
          await response
            .text()
            .catch(() => "");

        throw new Error(
          detail ||
            `TTS backend failed (${response.status}).`
        );
      }

      const contentType =
        String(
          response.headers.get(
            "content-type"
          ) || ""
        ).toLowerCase();

      if (
        !contentType.includes(
          "application/octet-stream"
        )
      ) {
        throw new Error(
          `Unexpected TTS stream type: ${
            contentType ||
            "missing"
          }`
        );
      }

      if (!response.body) {
        throw new Error(
          "TTS backend returned no readable stream."
        );
      }

      reader =
        response.body.getReader();

      armStreamStallTimeout();

      while (true) {
        const {
          done,
          value,
        } = await reader.read();

        clearStallTimeout();

        if (
          requestId !==
          ttsRequestIdRef.current
        ) {
          try {
            await reader.cancel();
          } catch {}
          return;
        }

        if (done) {
          break;
        }

        const chunk =
          value instanceof
          Uint8Array
            ? value
            : new Uint8Array(
                value || 0
              );

        if (!chunk.length) {
          armStreamStallTimeout();
          continue;
        }

        receivedAudioBytes +=
          chunk.length;

        const hadScheduledAudio =
          scheduledBackendAudio;

        pcmQueueBytes =
          concatUint8Arrays(
            pcmQueueBytes,
            chunk
          );

        drainPcmQueue();

        if (
          !hadScheduledAudio &&
          scheduledBackendAudio
        ) {
          console.log(
            "[TTS] backend PCM playback started",
            {
              provider:
                "google-cloud",
              model:
                "Chirp3-HD",
              voice:
                "Erinome",
              gender:
                "FEMALE",
              dominantLanguageHint,
              startBufferMs:
                TTS_PCM_START_BUFFER_MS,
              playbackBlockMs:
                TTS_PCM_PLAYBACK_BLOCK_MS,
              minimumScheduleLeadMs:
                TTS_PCM_MIN_SCHEDULE_LEAD_MS,
            }
          );
        }

        armStreamStallTimeout();
      }

      clearStallTimeout();

      // Very short answers or the final partial PCM block may finish below the
      // normal buffering thresholds. Flush the remaining queue once the network
      // stream is complete.
      drainPcmQueue({
        force: true,
      });

      if (
        !receivedAudioBytes ||
        !scheduledBackendAudio
      ) {
        throw new Error(
          "TTS backend returned no playable PCM audio."
        );
      }

      // A single trailing byte cannot form a signed 16-bit PCM sample.
      if (oddByteCarry.length) {
        console.warn(
          "[TTS] ignored incomplete trailing PCM byte",
          {
            bytes:
              oddByteCarry.length,
          }
        );
      }

      ttsStreamCompleteRef.current =
        true;

      console.log(
        "[TTS] backend PCM stream complete",
        {
          provider:
            "google-cloud",
          audio:
            "pcm_s16le",
          sampleRate:
            TTS_PCM_SAMPLE_RATE_HZ,
          channels:
            TTS_PCM_CHANNELS,
          receivedAudioBytes,
          dominantLanguageHint,
          startBufferMs:
            TTS_PCM_START_BUFFER_MS,
          playbackBlockMs:
            TTS_PCM_PLAYBACK_BLOCK_MS,
          minimumScheduleLeadMs:
            TTS_PCM_MIN_SCHEDULE_LEAD_MS,
        }
      );

      // If the final source already ended by the time the network closes,
      // clean the AudioContext immediately.
      if (
        ttsPcmSourcesRef.current.size ===
          0 &&
        ttsAudioContextRef.current ===
          audioContext
      ) {
        ttsAudioContextRef.current =
          null;
        ttsNextPlaybackTimeRef.current =
          0;

        try {
          const closing =
            audioContext.close?.();

          if (
            closing &&
            typeof closing.catch ===
              "function"
          ) {
            closing.catch(() => {});
          }
        } catch {}
      }
    } catch (error) {
      if (
        requestId !==
        ttsRequestIdRef.current
      ) {
        return;
      }

      const timedOut =
        error?.name ===
        "AbortError";

      console.warn(
        "[TTS] streaming backend failed; using browser fallback:",
        timedOut
          ? "backend request/stream timed out"
          : error?.message ||
              error,
        {
          receivedAudioBytes,
          backendPlaybackHadStarted:
            scheduledBackendAudio,
        }
      );

      // If a stream dies after partial Erinome playback, stop the partial queue
      // and restart the answer through the emergency browser voice. This keeps
      // the user from being left with a silently truncated answer.
      clearTtsPcmPlayback();
      fallbackToBrowser();
    } finally {
      clearFirstAudioTimeout();
      clearStallTimeout();

      if (
        requestId ===
          ttsRequestIdRef.current &&
        ttsAbortRef.current ===
          controller
      ) {
        ttsAbortRef.current =
          null;
      }
    }
  };

  // Stop read-aloud when this ChatInterface unmounts.
  useEffect(() => {
    return () => {
      ttsRequestIdRef.current +=
        1;

      try {
        ttsAbortRef.current?.abort?.();
      } catch {}

      try {
        if (
          typeof window !==
          "undefined"
        ) {
          window.speechSynthesis?.cancel?.();
        }
      } catch {}

      const sources =
        ttsPcmSourcesRef.current ||
        new Set();

      for (const source of sources) {
        try {
          source.onended = null;
          source.stop?.(0);
        } catch {}

        try {
          source.disconnect?.();
        } catch {}
      }

      sources.clear();

      const context =
        ttsAudioContextRef.current;

      ttsAudioContextRef.current =
        null;
      ttsNextPlaybackTimeRef.current =
        0;
      ttsStreamCompleteRef.current =
        false;
      ttsAbortRef.current = null;
      ttsUtterRef.current = null;

      if (context) {
        try {
          const closing =
            context.close?.();

          if (
            closing &&
            typeof closing.catch ===
              "function"
          ) {
            closing.catch(() => {});
          }
        } catch {}
      }
    };
  }, []);
  useEffect(() => {
    stopActiveTts();
  }, [sessionId, stopActiveTts]);

  const handleCopy = (text) => {
    try {
      navigator.clipboard.writeText(text);
    } catch {}
  };
  const handleEdit = (text) => {
    setInput((prev) => (prev ? `${prev}\n${text}` : text));
    lastActivityMsRef.current = Date.now();

    requestAnimationFrame(() => {
      try {
        textareaRef.current?.focus?.();
      } catch {}
    });
  };

  const normalizeFrameIdsFromDraft = (draft) => {
    try {
      const one = String(draft?.frameId || draft?.frame_id || draft?.frameID || "").trim() || "";

      let many = draft?.frameIds || draft?.frame_ids || draft?.frames || draft?.frameIDs || [];

      if (!Array.isArray(many)) many = [];

      const cleaned = many.map((x) => String(x || "").trim()).filter(Boolean);
      const all = Array.from(new Set([one, ...cleaned].filter(Boolean)));

      return {
        frameId: one || all[0] || "",
        frameIds: all,
      };
    } catch {
      return { frameId: "", frameIds: [] };
    }
  };

  // ✅ Send via backend ONLY (route switch: if attachment exists -> AnalyzeRoute; else -> ChatRoute)
  // ✅ NOW supports optional override text (used for 1-click workflows elsewhere; NOT used by voice accept)
  const handleSend = async (overrideText = null) => {
    // ✅ parent busy means NO sending, period
    if (parentBusy) return;

    if (sendLockRef.current) return;

    // Respect core blocks (but do not rely on canSend because overrideText may bypass input state)
    if (hardDisabled) return;
    if (!effectiveUid) return;
    if (!sessionId) return;
    if (isUploading) return;
    if (isSending) return;

    lastActivityMsRef.current = Date.now();

    const overrideTrim = typeof overrideText === "string" ? overrideText.trim() : "";
    const text = overrideTrim || input.trim();

    // ✅ In final minute: ignore attachments entirely and proceed text-only
    const savedDraft = VISION_MANUAL_ATTACHMENTS_ENABLED && !inFinalMinute ? draftAttachment : null;

    const { frameId: draftFrameId, frameIds: draftFrameIds } = normalizeFrameIdsFromDraft(savedDraft);

    const fileUrl = savedDraft?.fileUrl || null;
    const fileUrls = Array.isArray(savedDraft?.fileUrls)
      ? savedDraft.fileUrls.filter(Boolean)
      : fileUrl
      ? [fileUrl]
      : [];

    const hasAnyAttachment =
      !!draftFrameId ||
      (Array.isArray(draftFrameIds) && draftFrameIds.length > 0) ||
      !!fileUrl ||
      (Array.isArray(fileUrls) && fileUrls.length > 0);

    // ✅ Final minute: must have text (no attachment-only send)
    const finalText = text || (hasAnyAttachment ? "Please analyze the attached file." : "");
    if (!finalText) return;

    const attachment = savedDraft
      ? {
          fileUrl: savedDraft.fileUrl || null,
          fileUrls: fileUrls,
          fileName: savedDraft.file?.name || savedDraft.fileName || null,
          previewUrl: savedDraft.previewUrl || null,
          mime: savedDraft.file?.type || savedDraft.mime || null,

          // ✅ include both camel + snake for safety
          frameId: draftFrameId || null,
          frameIds: Array.isArray(draftFrameIds) ? draftFrameIds : [],
          frame_id: draftFrameId || null,
          frame_ids: Array.isArray(draftFrameIds) ? draftFrameIds : [],
        }
      : null;

    // optimistic user message identifiers
    const clientCreatedAt = Date.now();

    // ✅ Baseline is server-aligned max (sortMs)
    const baselineServer = Number(lastServerCreatedMsRef.current || 0);

    // ✅ Local sortMs: keep it just after server baseline (NOT Date.now),
    // so local echo never jumps around and never ends up after assistant due to clock skew.
    const prevMax = Number(
      messagesRef.current?.reduce?.(
        (acc, m) => Math.max(acc, Number(m?.sortMs || m?.createdMs || 0)),
        0
      ) || 0
    );
    const base = Math.max(baselineServer + 1, prevMax + 1, Number(nextLocalSortMsRef.current || 0));
    const nextLocal = base + 1;
    nextLocalSortMsRef.current = nextLocal;

    const localMsg = {
      id: `local_${clientCreatedAt}_${Math.random().toString(16).slice(2)}`,
      role: "user",
      name: "You",
      text: finalText,
      ts: new Date(nextLocal).toISOString(),

      createdMs: nextLocal,
      serverCreatedMs: 0,
      sortMs: nextLocal,

      clientCreatedAt,

      // show something in the bubble immediately
      image_url: fileUrl || null,
      file_name: attachment?.fileName || null,
      mime: attachment?.mime || null,

      // ✅ if we have frame ids, store them so UI can re-sign and render a compact strip
      frame_id: draftFrameId || draftFrameIds?.[0] || null,
      frame_ids: Array.isArray(draftFrameIds) ? draftFrameIds : [],
      image_urls: Array.isArray(fileUrls) ? fileUrls : fileUrl ? [fileUrl] : [],

      session_doc_path: null,
      meta: { client_created_at: clientCreatedAt },
      raw: { __local: true, client_created_at: clientCreatedAt },
    };

    // ✅ Register pending local send for robust dedupe
    try {
      const textNorm = _normText(finalText);
      const fid = String(draftFrameId || "").trim();
      pendingLocalRef.current.set(clientCreatedAt, {
        localId: localMsg.id,
        textNorm,
        frameId: fid,
        textSig: _makeUserSig(textNorm, fid),
        baselineServer: baselineServer || 0,
      });
    } catch {}

    sendLockRef.current = true;

    setMessages((prev) => [...prev, localMsg]);

    // clear input immediately
    setInput("");
    resetTextareaHeight();
    setIsSending(true);
    setIsLaunching(true);

    // clear draft immediately (user expectation)
    setDraftAttachment(null);

    awaitingBusyUserAnchorRef.current = false;
    responseAnchorActiveRef.current = true;
    responseAnchorIdRef.current = localMsg.id;

    scrollIntentRef.current = {
      type: "anchor",
      messageId: localMsg.id,
      behavior: "smooth",
      clearJump: true,
    };
    setFeedSig(`local_send:${clientCreatedAt}`);

    try {
      // ✅ Final minute: always text-only route (never analyze)
      if (hasAnyAttachment && !inFinalMinute) {
        // -----------------------------------------
        // ✅ PRINT LOG (what we're sending to Analyze)
        // -----------------------------------------
        const allFrameIds = _uniqCleanStrArray([draftFrameId, ...(draftFrameIds || [])]);
        const allFileUrls = _uniqCleanStrArray(fileUrls || []);
        _chatDbg("[VisionChatInterface][ANALYZE][SEND]", {
          sessionId: _trimStr(sessionId),
          assistMode: assistMode || "B",
          user_text_len: String(finalText || "").length,
          frame_id_present: !!_trimStr(draftFrameId),
          frame_ids_count: allFrameIds.length,
          fileUrls_count: allFileUrls.length,
          // redacted examples (avoid dumping signed URLs)
          frame_ids_sample: allFrameIds.slice(0, 4),
          fileUrls_sample: allFileUrls.slice(0, 2).map(_redactUrl),
        });

        await sendVisionChatMessage({
          sessionId,
          text: finalText,
          attachment,
          assistMode,

          // triggers dedicated /analyze inside API helper
          action: "analyze",

          // also pass explicit ids for the upgraded API to forward
          frame_id: draftFrameId || "",
          frame_ids: Array.isArray(draftFrameIds) ? draftFrameIds : [],
          image_urls: Array.isArray(fileUrls) ? fileUrls : [],
          meta: { client_created_at: clientCreatedAt },
        });
      } else {
        // ✅ ChatRoute: text-only
        await sendVisionChatMessage({
          sessionId,
          text: finalText,
          attachment: null,
          assistMode,
          meta: { client_created_at: clientCreatedAt },
        });
      }

      // Keep the viewport pinned to the user-turn anchor while Lexa works.
      setFeedSig(`sent:${Date.now()}`);
    } catch (e) {
      // ✅ remove pending registry entry on failure (so it doesn't stick forever)
      try {
        pendingLocalRef.current.delete(clientCreatedAt);
      } catch {}

      setMessages((prev) => [
        ...prev,
        {
          id: `local_err_${Date.now()}`,
          role: "assistant",
          name: "Lexa",
          text: `Error: ${e?.message || "Failed to send"}`,
          ts: new Date().toISOString(),
          createdMs: Math.max(Number(lastServerCreatedMsRef.current || 0) + 2, Date.now()),
          sortMs: Math.max(Number(lastServerCreatedMsRef.current || 0) + 2, Date.now()),
          image_url: null,
          file_name: null,
          mime: null,
          frame_id: null,
          session_doc_path: null,
          raw: {},
        },
      ]);

      // Keep the failed turn anchored as well; do not jump the user to the bottom.
      setFeedSig(`err:${Date.now()}`);
    } finally {
      setIsSending(false);
      sendLockRef.current = false;
      setTimeout(() => setIsLaunching(false), 300);
    }
  };

  const onKeyDown = (e) => {
    // ✅ Parent busy => Enter must not send
    if (parentBusy) return;
    if (hardDisabled) return;

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Show busy bubble only if:
  // - parent says busy
  // - and we're not inside the post-assistant delay window
  const showBusyBubble = useMemo(() => {
    if (!uiBusyMode) return false;
    if (!busyDelayUntilMs) return true;
    return Date.now() >= busyDelayUntilMs;
  }, [uiBusyMode, busyDelayUntilMs]);

  const showWave = recording || voiceReady || isTranscribing || isAcceptingVoice;

  const composerRef = useRef(null);
  const [composerHeight, setComposerHeight] = useState(120);

  useEffect(() => {
    const el = composerRef.current;
    if (!el) return undefined;

    const measure = () => {
      try {
        const next = Math.max(0, Math.round(el.getBoundingClientRect().height || 0));
        if (next > 0) setComposerHeight(next);
      } catch {}
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const historyNavRight = "14px";
  const historyLoadPreviousTop = "14px";
  const jumpToLatestBottom = `${Math.max(76, composerHeight + 14)}px`;
  const historyPreviousDisabled = Boolean(
    historyLoadingPrevious || parentBusy || isSending
  );

  return (
    <div style={styles.wrap}>
      <VisionNoticeDialog
        notice={notice}
        onClose={closeVisionNotice}
      />

      <FileViewerModal
        open={!!transcriptRecord}
        onClose={closeTranscriptViewer}
        record={transcriptRecord}
      />

      <div style={styles.log} ref={logRef} onScroll={onLogScroll}>
        {messages
          .filter((m) => !m?.__hidden)
          .map((m) => (
            <div
              key={m.id}
              ref={(node) => registerMessageNode(m.id, node)}
              style={styles.messageAnchorWrap}
            >
              <MessageBubble
                m={m}
                onSpeak={handleSpeak}
                onCopy={handleCopy}
                onEdit={handleEdit}
                getResolvedFrameMedia={getResolvedFrameMedia}
                onImageError={handleImageError}
                highlighted={
                  m.role === "assistant" &&
                  String(m.id || "") === String(highlightedHistoryMessageId || "")
                }
              />
            </div>
          ))}

        {/* Persistent busy bubble from parent props. It is the temporary Lexa
            anchor for EVERY response cycle: text chat and screen analysis. */}
        {showBusyBubble && (
          <div
            ref={busyBubbleRef}
            style={{
              ...styles.bubble,
              ...styles.bubbleAssistant,
              ...styles.busyBubble,
            }}
          >
            <div style={styles.bubbleHeader}>
              <strong>Lexa</strong>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={styles.dots}>
                <span style={styles.dot} />
                <span style={styles.dot} />
                <span style={styles.dot} />
              </div>

              <em style={styles.busyText}>
                {uiBusyMode === "analyzing" ? "Analyzing…" : "Thinking…"}
              </em>
            </div>
          </div>
        )}
      </div>

      {/* ↑ loads the next older 40 messages from backend history. */}
      {sessionId && historyHasMore ? (
        <button
          type="button"
          className="no-print asklawly-history-nav-button"
          aria-label="Load previous messages"
          title="Load previous messages"
          disabled={historyPreviousDisabled}
          onClick={loadPreviousVisionHistory}
          style={{
            ...styles.historyNavButton,
            top: historyLoadPreviousTop,
            right: historyNavRight,
            ...(historyPreviousDisabled
              ? styles.historyNavButtonDisabled
              : null),
          }}
        >
          {historyLoadingPrevious ? (
            <span
              aria-hidden="true"
              style={styles.historyNavSpinner}
            />
          ) : (
            <FiChevronUp aria-hidden="true" />
          )}
        </button>
      ) : null}

      {/* ↓ matches ChatInterface and is frontend-only: jump to newest message. */}
      {showJumpToLatest && messages.length > 0 ? (
        <button
          type="button"
          className="no-print asklawly-history-nav-button"
          aria-label="Jump to latest message"
          title="Jump to latest message"
          onClick={jumpToLatest}
          style={{
            ...styles.historyNavButton,
            bottom: jumpToLatestBottom,
            right: historyNavRight,
          }}
        >
          <FiChevronDown aria-hidden="true" />
        </button>
      ) : null}

      <div ref={composerRef} style={styles.composer}>
        <div style={styles.composerCard}>
          <div style={{ position: "relative", width: "100%" }}>
            {showWave ? (
              <div style={styles.waveWrap}>
                <div style={styles.waveRow}>
                  {waveformData.map((v, i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: `${v * 2}px`,
                        backgroundColor: "#555",
                        marginTop: `${10 - v / 2}px`,
                        marginBottom: `${10 - v / 2}px`,
                        borderRadius: 1,
                        transition: "height 90ms linear, margin 90ms linear",
                        willChange: "height, margin",
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => {
                  lastActivityMsRef.current = Date.now();
                  setInput(e.target.value);
                }}
                onKeyDown={onKeyDown}
                placeholder={
                  !effectiveUid ? "Log in to chat" : sessionEnded ? "Session ended" : "Ask your question"
                }
                // ✅ Disable when session ended OR hardDisabled OR busy
                disabled={hardDisabled || parentBusy || !effectiveUid || isUploading}
                style={styles.textarea}
              />
            )}
          </div>

          {/* ✅ If final minute: ensure no draft attachment UI is shown */}
          {VISION_MANUAL_ATTACHMENTS_ENABLED && !inFinalMinute && draftAttachment && (
            <div style={styles.draftChip}>
              {draftAttachment.previewUrl ? (
                <img
                  src={draftAttachment.previewUrl}
                  alt=""
                  style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <FileIcon fileName={draftAttachment.file?.name || "file"} size={36} alt="File" />
              )}

              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={styles.draftName} title={draftAttachment.file?.name || ""}>
                  {draftAttachment.file?.name || "attachment"}
                </span>

                {typeof draftAttachment.progress === "number" &&
                  draftAttachment.progress > 0 &&
                  draftAttachment.progress < 100 && (
                    <div style={styles.progressOuter}>
                      <div style={{ ...styles.progressInner, width: `${draftAttachment.progress}%` }} />
                    </div>
                  )}
              </div>

              <button
                type="button"
                onClick={() => setDraftAttachment(null)}
                style={styles.draftRemove}
                title="Remove"
                disabled={hardDisabled || parentBusy}
              >
                ✖
              </button>
            </div>
          )}

          <div style={styles.actions}>
            <div style={styles.actionsLeft}>
              {/* Plus is a Vision utility menu. Manual attachments are disabled;
                  Chat transcript remains available throughout the session. */}
              {
                <>
                  <button
                    ref={plusButtonRef}
                    type="button"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      setShowAttachmentMenu((p) => !p);
                      lastActivityMsRef.current = Date.now();
                    }}
                    style={styles.iconBtn}
                    title="More options"
                    aria-label="More options"
                    disabled={!effectiveUid || !sessionId}
                  >
                    <FaPlus />
                  </button>

                  <AttachmentMenu
                    open={showAttachmentMenu}
                    onClose={() => setShowAttachmentMenu(false)}
                    fileInputRef={fileInputRef}
                    anchorRef={plusButtonRef}
                    onOpenChatTranscript={handleOpenVisionTranscript}
                    sessionKind="vision_assist"
                  />

                  {VISION_MANUAL_ATTACHMENTS_ENABLED && !!effectiveUid && (
                    <AttachmentController
                      fileInputRef={fileInputRef}
                      uid={effectiveUid}
                      email={effectiveUser?.email || ""}
                      sessionKind="vision_assist"
                      sessionId={sessionId}
                      userType={
                        userTypeProp ||
                        effectiveSession?.user_type ||
                        effectiveSession?.owner_type ||
                        "individual"
                      }
                      onUploadingChange={(v) => {
                        setIsUploading(!!v);
                        // Optional: if upload starts, make sure we have a draft container
                        if (!!v && !inFinalMinute) {
                          setDraftAttachment((prev) => prev || { progress: 0, file: { name: "uploading…", type: "" } });
                        }
                      }}
                      onAttachmentReady={(att) => {
                        // ✅ Safety: if we crossed into final minute mid-upload/callback, ignore attachments.
                        if (inFinalMinute) return;

                        // ✅ IMPORTANT: Do NOT "re-attach" / resurrect a cleared draft.
                        // If user already sent (we setDraftAttachment(null)) or removed it, ignore late callbacks.
                        setDraftAttachment((prev) => {
                          if (!prev && !isUploading) return prev;

                          const prevObj = prev || {};

                          const fid = String(att?.frameId || att?.frame_id || "").trim();
                          const fidsRaw = att?.frameIds || att?.frame_ids || att?.frames || [];
                          const fids = Array.isArray(fidsRaw)
                            ? fidsRaw.map((x) => String(x || "").trim()).filter(Boolean)
                            : [];

                          const mergedFrameIds = Array.from(
                            new Set(
                              [
                                String(prevObj?.frameId || prevObj?.frame_id || "").trim(),
                                ...(Array.isArray(prevObj?.frameIds) ? prevObj.frameIds : []),
                                fid,
                                ...fids,
                              ].filter(Boolean)
                            )
                          );

                          return {
                            ...prevObj,
                            previewUrl: att.previewUrl || prevObj.previewUrl || null,
                            fileUrl: att.fileUrl || prevObj.fileUrl || null,
                            fileUrls: att.fileUrls || prevObj.fileUrls || [],
                            file: {
                              name: att.name || prevObj?.file?.name || "attachment",
                              type: att.mime || prevObj?.file?.type || "",
                            },
                            mime: att.mime || prevObj.mime || "",
                            progress: typeof att.progress === "number" ? att.progress : prevObj.progress || 0,

                            // ✅ persist frame ids (for AnalyzeRoute)
                            frameId: fid || prevObj.frameId || prevObj.frame_id || null,
                            frameIds: mergedFrameIds,
                          };
                        });

                        lastActivityMsRef.current = Date.now();
                      }}
                    />
                  )}
                </>
              }
            </div>

            <div style={styles.actionsRight}>
              {showWave ? (
                <>
                  <button
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      cancelVoice();
                    }}
                    type="button"
                    style={styles.iconBtn}
                    title="Cancel voice"
                    disabled={hardDisabled || parentBusy || isTranscribing || isAcceptingVoice}
                  >
                    <FaTimes />
                  </button>

                  <button
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      acceptVoice();
                    }}
                    type="button"
                    style={styles.iconBtn}
                    title={recording ? "Stop & transcribe" : "Transcribe"}
                    disabled={
                      hardDisabled ||
                      parentBusy ||
                      isTranscribing ||
                      isAcceptingVoice ||
                      (!recording && !voiceReady)
                    }
                  >
                    {/* ✅ Spinner replaces ✓ while accepting/transcribing (works on iOS/Android/Desktop) */}
                    {isTranscribing || isAcceptingVoice ? <Spinner size={16} /> : <FaCheck />}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onPointerDown={handleVoiceInput}
                    style={styles.iconBtn}
                    title="Speak"
                    disabled={hardDisabled || parentBusy || !effectiveUid}
                  >
                    <FiMic />
                  </button>

                  <button
                    type="button"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      handleSend();
                    }}
                    // ✅ Send is disabled strictly when parentBusy OR hardDisabled
                    disabled={!canSend || parentBusy || hardDisabled}
                    style={{
                      ...styles.sendBtn,
                      backgroundColor:
                        !canSend || parentBusy || hardDisabled
                          ? "#CBD5E1"
                          : "#1E2A78",
                      cursor:
                        !canSend || parentBusy || hardDisabled
                          ? "not-allowed"
                          : "pointer",
                      transition: "transform 0.25s ease, box-shadow 0.25s ease",
                      boxShadow: isLaunching
                        ? "0 0 0 4px rgba(11, 95, 255, 0.08)"
                        : "none",
                      position: "relative",
                      overflow: "hidden",
                    }}
                    title="Send"
                  >
                    <FiArrowUp
                      style={{
                        fontSize: 22,
                        color: "#fff",
                        animation: isLaunching
                          ? "asklawlySendLift 1.05s ease-in-out infinite"
                          : "none",
                        opacity: isLaunching ? 0.9 : 1,
                        transition: "opacity 160ms ease",
                        position: "relative",
                        zIndex: 2,
                      }}
                    />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {VISION_MANUAL_ATTACHMENTS_ENABLED && isUploading && !inFinalMinute && (
          <div style={styles.uploadHint}>Uploading… (send will work once your file finishes)</div>
        )}

        {(isTranscribing || isAcceptingVoice) && (
          <div style={styles.transcribingHint}>Transcribing…</div>
        )}

        {/* ✅ Step 3: dot animation keyframes (keep inside component) */}
        <style>
          {`
            @keyframes asklawlyDotPulse {
              0% { transform: translateY(0); opacity: 0.35; }
              50% { transform: translateY(-2px); opacity: 0.9; }
              100% { transform: translateY(0); opacity: 0.35; }
            }
          `}
        </style>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    overflow: "hidden",
    background: "#FFFFFF",
    color: "#0F172A",
    fontFamily: "sans-serif",
  },

  lockBanner: {
    padding: "10px 12px",
    background: "#fff7ed",
    borderBottom: "1px solid #fed7aa",
    color: "#9a3412",
    fontSize: 13,
    flex: "0 0 auto",
  },

  log: {
    position: "relative",
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    padding: "14px 14px 18px",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    scrollBehavior: "auto",
  },

  historyNavButton: {
    position: "absolute",
    zIndex: 7,
    width: 38,
    height: 38,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.34)",
    background: "#1E2A78",
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
    boxShadow: "0 7px 18px rgba(15, 23, 42, 0.18)",
    WebkitTapHighlightColor: "transparent",
  },
  historyNavButtonDisabled: {
    cursor: "default",
    opacity: 0.62,
  },
  historyNavSpinner: {
    width: 16,
    height: 16,
    borderRadius: 999,
    border: "2px solid rgba(255,255,255,0.42)",
    borderTopColor: "#FFFFFF",
    animation: "chatSpin 0.8s linear infinite",
    boxSizing: "border-box",
  },

  messageAnchorWrap: {
    width: "100%",
  },

  bubble: {
    padding: "11px 14px",
    borderRadius: 16,
    maxWidth: "88%",
    fontSize: 15,
    lineHeight: 1.5,
    boxSizing: "border-box",
  },
  bubbleUser: {
    background: "#EFF6FF",
    border: "1px solid #BFDBFE",
    color: "#0F172A",
    boxShadow: "0 6px 16px rgba(37,99,235,0.05)",
  },
  bubbleAssistant: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    color: "#0F172A",
    boxShadow: "0 8px 22px rgba(15,23,42,0.06)",
    transition:
      "border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease",
  },
  bubbleAssistantAnchorHighlight: {
    background: "#F8FAFF",
    borderColor: "#93C5FD",
    boxShadow:
      "0 0 0 3px rgba(37,99,235,0.14), 0 10px 28px rgba(15,23,42,0.08)",
  },
  busyBubble: {
    marginBottom: 16,
    width: "fit-content",
  },
  busyText: {
    opacity: 0.82,
    color: "#475569",
    fontStyle: "normal",
    fontSize: 13,
    fontWeight: 750,
  },
  bubbleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 7,
    flexWrap: "wrap",
  },
  bubbleName: {
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 950,
  },
  iconRow: { display: "flex", gap: 9, alignItems: "center" },
  icon: { cursor: "pointer", fontSize: 15, color: "#64748B" },
  fileLink: {
    textDecoration: "none",
    color: "#1E2A78",
    backgroundColor: "#EEF2FF",
    border: "1px solid #C7D2FE",
    padding: "7px 11px",
    borderRadius: 10,
    display: "inline-block",
    fontSize: 12,
    fontWeight: 850,
  },

  singleScreenBlock: {
    marginTop: 10,
  },
  messageScreenImage: {
    display: "block",
    width: "100%",
    maxWidth: 520,
    maxHeight: 240,
    objectFit: "contain",
    borderRadius: 12,
    border: "1px solid #CBD5E1",
    background: "#F8FAFC",
  },
  screenLoading: {
    minHeight: 62,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: "1px dashed #CBD5E1",
    background: "#F8FAFC",
    color: "#64748B",
    fontSize: 12,
    fontWeight: 750,
  },
  screenStripBlock: {
    marginTop: 10,
  },
  screenStripLabel: {
    marginBottom: 6,
    color: "#64748B",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  screenStrip: {
    display: "flex",
    gap: 7,
    overflowX: "auto",
    maxWidth: "100%",
    paddingBottom: 2,
    WebkitOverflowScrolling: "touch",
  },
  screenThumbLink: {
    position: "relative",
    flex: "0 0 92px",
    width: 92,
    height: 64,
    overflow: "hidden",
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    background: "#F8FAFC",
    textDecoration: "none",
    boxShadow: "0 3px 10px rgba(15,23,42,0.06)",
  },
  screenThumbImg: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  screenThumbPlaceholder: {
    position: "relative",
    flex: "0 0 92px",
    width: 92,
    height: 64,
    display: "grid",
    placeItems: "center",
    borderRadius: 10,
    border: "1px dashed #CBD5E1",
    background: "#F8FAFC",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 800,
  },
  screenThumbIndex: {
    position: "absolute",
    right: 4,
    bottom: 4,
    minWidth: 18,
    height: 18,
    padding: "0 4px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#1E2A78",
    color: "#FFFFFF",
    border: "2px solid #FFFFFF",
    fontSize: 10,
    fontWeight: 950,
    lineHeight: 1,
  },

  dots: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "#1E2A78",
    opacity: 0.6,
    animation: "asklawlyDotPulse 1.2s infinite ease-in-out",
  },

  composer: {
    borderTop: "1px solid #E2E8F0",
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: "0 0 auto",
    background: "#FFFFFF",
  },

  composerCard: {
    width: "100%",
    border: "1px solid #CBD5E1",
    borderRadius: 18,
    backgroundColor: "#FFFFFF",
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    boxSizing: "border-box",
    boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  },

  textarea: {
    width: "100%",
    fontSize: 15,
    color: "#0F172A",
    border: "none",
    outline: "none",
    resize: "vertical",
    minHeight: TEXTAREA_DEFAULT_HEIGHT,
    maxHeight: TEXTAREA_MAX_HEIGHT,
    overflowY: "hidden",
    lineHeight: 1.5,
    fontFamily: "inherit",
    boxSizing: "border-box",
    padding: "3px 2px",
    display: "block",
    background: "transparent",
  },

  waveWrap: {
    height: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  waveRow: {
    display: "flex",
    width: "100%",
    gap: 2,
  },

  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 0,
  },
  actionsLeft: { display: "flex", alignItems: "center", gap: 10 },
  actionsRight: { display: "flex", alignItems: "center", gap: 10 },

  iconBtn: {
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F8FAFC",
    color: "#1E2A78",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: 0,
    cursor: "pointer",
    fontSize: 16,
    boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
  },
  sendBtn: {
    border: "1px solid #1E2A78",
    borderRadius: "50%",
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  uploadHint: { fontSize: 12, color: "#666", paddingLeft: 2 },

  draftChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    borderRadius: 10,
    background: "#f2f2f2",
    border: "1px solid #ddd",
    maxWidth: "100%",
  },
  draftName: {
    fontSize: 12,
    fontWeight: 600,
    maxWidth: 260,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  progressOuter: { width: 160, height: 4, background: "#eee", borderRadius: 2 },
  progressInner: { height: "100%", background: "#2563eb", borderRadius: 2 },
  draftRemove: { border: "none", background: "transparent", cursor: "pointer", fontSize: 18 },

  voiceRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    border: "1px solid #ddd",
    borderRadius: 14,
    padding: "10px 12px",
  },
  voicePreview: { flex: 1, color: "#333", fontSize: 14 },

  transcribingHint: {
    marginTop: 0,
    fontSize: 12,
    color: "#6B7280",
    paddingLeft: 2,
  },

  noticeOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 2147483200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "rgba(15, 23, 42, 0.46)",
    boxSizing: "border-box",
  },
  noticeCard: {
    width: "min(440px, 100%)",
    borderRadius: 18,
    background: "#ffffff",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
    padding: 26,
    color: "#0f172a",
    boxSizing: "border-box",
  },
  noticeTitle: {
    fontSize: 19,
    lineHeight: 1.3,
    fontWeight: 700,
    marginBottom: 10,
  },
  noticeMessage: {
    fontSize: 15,
    lineHeight: 1.6,
    color: "#475569",
    whiteSpace: "pre-wrap",
  },
  noticeSpinnerRow: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginTop: 16,
  },
  noticeSpinner: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: "2px solid rgba(15,23,42,0.18)",
    borderTopColor: "#0f172a",
    animation: "asklawlySpin360 0.85s linear infinite",
    flex: "0 0 16px",
  },
  noticeHint: {
    fontSize: 12,
    lineHeight: 1.4,
    color: "#6B7280",
  },
  noticeActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 22,
  },
  noticeButton: {
    border: 0,
    borderRadius: 10,
    padding: "10px 18px",
    background: "#0f172a",
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
};
